import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  DatePicker,
  Select,
  Table,
  Space,
  Spin,
  Alert,
  Tag,
  Tooltip,
} from 'antd';
import {
  UsergroupAddOutlined,
  LineChartOutlined,
  ShoppingCartOutlined,
  CalculatorOutlined,
  DollarOutlined,
  TrophyOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { dayjs } from '@src/utils/dayjs';
import { api } from '@src/utils/axios';
import { useCategoryRender } from '@src/pages/offerings/hooks/useCategoryRender';
import './DashboardStats.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Type definitions for API response
interface ApiResponse {
  error: boolean;
  message: string;
  data: {
    patientOverview?: {
      patients: {
        new: number;
        old: number;
        total: number;
      };
      onlyConsultation: {
        new: number;
        old: number;
        total: number;
        newPercentage: number;
        oldPercentage: number;
        totalPercentage: number;
      };
      // Backward compatible (older API)
      zeroClinicInvoice?: {
        new: number;
        old: number;
        total: number;
        newPercentage: number;
        oldPercentage: number;
        totalPercentage: number;
      };
      // New breakdowns (new API)
      onlyConsultationZeroInvoice?: {
        new: number;
        old: number;
        total: number;
        newPercentage: number;
        oldPercentage: number;
        totalPercentage: number;
      };
      onlyConsultationNonZeroInvoice?: {
        new: number;
        old: number;
        total: number;
        newPercentage: number;
        oldPercentage: number;
        totalPercentage: number;
      };
      clinicInvoiceCreated: {
        new: number;
        old: number;
        total: number;
        newPercentage: number;
        oldPercentage: number;
        totalPercentage: number;
      };
      clinicInvoiceZeroInvoice?: {
        new: number;
        old: number;
        total: number;
        newPercentage: number;
        oldPercentage: number;
        totalPercentage: number;
      };
      clinicInvoiceNonZeroInvoice?: {
        new: number;
        old: number;
        total: number;
        newPercentage: number;
        oldPercentage: number;
        totalPercentage: number;
      };
      medicineInvoiceCreated: {
        new: number;
        old: number;
        total: number;
        newPercentage: number;
        oldPercentage: number;
        totalPercentage: number;
      };
      treatmentTaken: {
        new: number;
        old: number;
        total: number;
        newPercentage: number;
        oldPercentage: number;
        totalPercentage: number;
      };
    };
    leadsOverview?: {
      totals: {
        total_leads: number;
        by_source: {
          GOOGLE: { count: number; percentage: number };
          META: { count: number; percentage: number };
          WALK_IN: { count: number; percentage: number };
          WALK_IN_REFERRAL: { count: number; percentage: number };
        };
      };
      status: Array<{
        status: string;
        GOOGLE: { count: number; percentage: number };
        META: { count: number; percentage: number };
        WALK_IN: { count: number; percentage: number };
        WALK_IN_REFERRAL: { count: number; percentage: number };
        ALL: { count: number; percentage: number };
      }>;
      interest: Array<{
        interest: string;
        GOOGLE: { count: number; percentage: number };
        META: { count: number; percentage: number };
        WALK_IN: { count: number; percentage: number };
        WALK_IN_REFERRAL: { count: number; percentage: number };
        ALL: { count: number; percentage: number };
      }>;
    };
    purchaseOverview?: {
      medicalPurchase: {
        final_amount: number;
        paid_amount: {
          cash: number;
          gpay: number;
          total: number;
        };
        due_amount: number;
      };
      insidePurchase: {
        final_amount: number;
        paid_amount: {
          cash: number;
          gpay: number;
          total: number;
        };
        due_amount: number;
      };
      total: {
        final_amount: number;
        paid_amount: {
          cash: number;
          gpay: number;
          total: number;
        };
        due_amount: number;
      };
    };
    saleOverview?: {
      onlyConsultation: {
        final_amount: string;
        discount_amount: string;
        total_amount: string;
        received_amount: {
          cash: string;
          gpay: string;
          total: string;
        };
        due_amount: string;
      };
      clinicInvoice: {
        final_amount: string;
        discount_amount: string;
        total_amount: string;
        received_amount: {
          cash: string;
          gpay: string;
          total: string;
        };
        due_amount: string;
      };
      medicalInvoice: {
        final_amount: string;
        discount_amount: string;
        total_amount: string;
        received_amount: {
          cash: string;
          gpay: string;
          total: string;
        };
        due_amount: string;
      };
      total: {
        final_amount: string;
        discount_amount: string;
        total_amount: string;
        received_amount: {
          cash: string;
          gpay: string;
          total: string;
        };
        due_amount: string;
      };
    };
    accountingOverview?: {
      start: string;
      end: string;
      salesReceived: {
        cash: number;
        gpay: number;
        total: number;
      };
      oldInvoicePayments: {
        cash: number;
        gpay: number;
        total: number;
      };
      PurchasePayments: {
        cash: number;
        gpay: number;
        total: number;
      };
      oldPurchasePayments: {
        cash: number;
        gpay: number;
        total: number;
      };
      staffSalaries: {
        cash: number;
        gpay: number;
        total: number;
      };
      rentUtilities: {
        cash: number;
        gpay: number;
        total: number;
      };
      marketingExpenses: {
        cash: number;
        gpay: number;
        total: number;
      };
      otherExpenses: {
        cash: number;
        gpay: number;
        total: number;
      };
      currentBalance: {
        cash: number;
        gpay: number;
        total: number;
      };
      patientsDue: {
        clinicInvoices: number;
        medicalInvoices: number;
        total: number;
      };
      purchasePendingPayment: {
        medicalPurchase: number;
        clinicExpenses: number;
        total: number;
      };
    };
    mostUsedOfferings?: {
      offerings: Array<{
        offering_id: number;
        offering_name: string;
        offering_category: string;
        total_quantity: string;
        usage_count: number;
        total_revenue: string;
      }>;
    };
  };
}

// Type definitions for Transaction Report API response
interface TransactionReportResponse {
  error: boolean;
  message: string;
  data: {
    clinic: {
      transactions: Array<{
        id: number;
        date: string;
        remark?: string;
        patient_name: string;
        treatment_name: string;
        patient_service_type: string;
        patient_status_type: string;
        consultation: {
          final_amount: number;
          discount_amount: number;
          discount_rate?: number;
          due_amount: number;
          cash?: number;
          gpay?: number;
          total?: number;
        };
        treatment: {
          final_amount: number;
          discount_amount: number;
          discount_rate?: number;
          due_amount: number;
          cash?: number;
          gpay?: number;
          total?: number;
        };
        medicine: {
          final_amount: number;
          discount_amount: number;
          discount_rate?: number;
          due_amount: number;
          cash?: number;
          gpay?: number;
          total?: number;
        };
        total_amount: number;
        total_discount: number;
        total_due: number;
        bill_amount?: number;
        old_paid_amount?: number;
        received_amount?: { cash?: number; gpay?: number; total?: number };
      }>;
      totals: {
        consultation: {
          final_amount: number;
          discount_amount: number;
          due_amount: number;
          cash?: number;
          gpay?: number;
          total?: number;
        };
        treatment: {
          final_amount: number;
          discount_amount: number;
          due_amount: number;
          cash?: number;
          gpay?: number;
          total?: number;
        };
        total: {
          final_amount: number;
          discount_amount: number;
          due_amount: number;
          bill_amount?: number;
          old_paid_amount?: number;
          received_amount?: { cash?: number; gpay?: number; total?: number };
        };
      };
    };
    medical: {
      transactions: Array<{
        id: number;
        date: string;
        remark?: string;
        patient_name: string;
        treatment_name: string;
        patient_service_type: string;
        patient_status_type: string;
        consultation: {
          final_amount: number;
          discount_amount: number;
          discount_rate?: number;
          due_amount: number;
          cash?: number;
          gpay?: number;
          total?: number;
        };
        treatment: {
          final_amount: number;
          discount_amount: number;
          discount_rate?: number;
          due_amount: number;
          cash?: number;
          gpay?: number;
          total?: number;
        };
        medicine: {
          final_amount: number;
          discount_amount: number;
          discount_rate?: number;
          due_amount: number;
          cash?: number;
          gpay?: number;
          total?: number;
          bill_amount?: number;
        };
        total_amount: number;
        total_discount: number;
        total_due: number;
      }>;
      totals: {
        medicine: {
          final_amount: number;
          discount_amount: number;
          due_amount: number;
          cash: number;
          gpay: number;
          total: number;
          bill_amount?: number;
        };
        total: {
          final_amount: number;
          discount_amount: number;
          due_amount: number;
          bill_amount?: number;
        };
      };
    };
  };
}

const DashboardStats: React.FC = () => {
  const getCategoryConfig = useCategoryRender();
  const [dateRange, setDateRange] = useState<any>(null);
  const [dateRangeOption, setDateRangeOption] = useState<string>('custom');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<
    ApiResponse['data'] | null
  >(null);

  // Transaction Report state
  const [transactionLoading, setTransactionLoading] = useState<boolean>(false);
  const [transactionError, setTransactionError] = useState<string | null>(null);
  const [transactionData, setTransactionData] = useState<
    TransactionReportResponse['data']['clinic']['transactions']
  >([]);
  const [transactionTotals, setTransactionTotals] = useState<
    TransactionReportResponse['data']['clinic']['totals'] | null
  >(null);
  const [medicalTransactionData, setMedicalTransactionData] = useState<
    TransactionReportResponse['data']['medical']['transactions']
  >([]);
  const [medicalTransactionTotals, setMedicalTransactionTotals] = useState<
    TransactionReportResponse['data']['medical']['totals'] | null
  >(null);
  const [hasUserSelectedDate, setHasUserSelectedDate] = useState<boolean>(false);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;

    try {
      setLoading(true);
      setError(null);

      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');

      const response = await api.get<ApiResponse>('/dashboard/report', {
        params: {
          startDate,
          endDate,
        },
      });

      if (response.error) {
        setError(response.message || 'Failed to load dashboard data');
      } else {
        setDashboardData(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load dashboard data'
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch data only when user selects a date (not on mount)
  useEffect(() => {
    // Only fetch if user has selected a date
    if (hasUserSelectedDate && dateRange && dateRange[0] && dateRange[1]) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRange?.[0]?.format('YYYY-MM-DD'),
    dateRange?.[1]?.format('YYYY-MM-DD'),
    hasUserSelectedDate,
  ]);

  // Handle date range change
  const handleDateRangeChange = (dates: any) => {
    setDateRange(dates);
    setHasUserSelectedDate(true); // Mark that user has selected a date
    if (dates && dates[0] && dates[1]) {
      setDateRangeOption('custom');
    }
  };

  // Handle quick date range option change
  const handleDateRangeOptionChange = (option: string) => {
    setDateRangeOption(option);
    setHasUserSelectedDate(true); // Mark that user has selected a date
    const today = dayjs();
    let newRange: any = null;

    switch (option) {
      case 'last_day':
        newRange = [
          today.subtract(1, 'day').startOf('day'),
          today.subtract(1, 'day').endOf('day'),
        ];
        break;
      case 'last_week':
        newRange = [
          today.subtract(1, 'week').startOf('week'),
          today.subtract(1, 'week').endOf('week'),
        ];
        break;
      case 'last_month':
        newRange = [
          today.subtract(1, 'month').startOf('month'),
          today.subtract(1, 'month').endOf('month'),
        ];
        break;
      case 'last_two_months':
        newRange = [
          today.subtract(2, 'month').startOf('month'),
          today.subtract(1, 'month').endOf('month'),
        ];
        break;
      case 'custom':
        // Keep current range
        return;
      default:
        newRange = [
          today.subtract(1, 'month').startOf('month'),
          today.subtract(1, 'month').endOf('month'),
        ];
    }

    if (newRange) {
      setDateRange(newRange);
    }
  };

  // Fetch transaction report data
  const fetchTransactionReport = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;

    try {
      setTransactionLoading(true);
      setTransactionError(null);

      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');

      const response = await api.get<TransactionReportResponse>(
        '/dashboard/transaction-report',
        {
          params: {
            startDate,
            endDate,
          },
        }
      );

      if (response.error) {
        setTransactionError(
          response.message || 'Failed to load transaction report'
        );
        setTransactionData([]);
        setTransactionTotals(null);
        setMedicalTransactionData([]);
        setMedicalTransactionTotals(null);
      } else {
        setTransactionData(response.data?.clinic?.transactions || []);
        setTransactionTotals(response.data?.clinic?.totals || null);
        setMedicalTransactionData(response.data?.medical?.transactions || []);
        setMedicalTransactionTotals(response.data?.medical?.totals || null);
      }
    } catch (err: any) {
      console.error('Error fetching transaction report:', err);
      setTransactionError(
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load transaction report'
      );
      setTransactionData([]);
      setTransactionTotals(null);
      setMedicalTransactionData([]);
      setMedicalTransactionTotals(null);
    } finally {
      setTransactionLoading(false);
    }
  };

  // Fetch transaction report only when user selects a date (not on mount)
  useEffect(() => {
    // Only fetch if user has selected a date
    if (hasUserSelectedDate && dateRange && dateRange[0] && dateRange[1]) {
      fetchTransactionReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRange?.[0]?.format('YYYY-MM-DD'),
    dateRange?.[1]?.format('YYYY-MM-DD'),
    hasUserSelectedDate,
  ]);

  // Format currency
  const formatCurrency = (
    amount: number | string | null | undefined
  ): string => {
    if (amount === null || amount === undefined) return '';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '';
    return `₹${numAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format number with percentage
  const formatWithPercent = (
    count: number,
    percent: number
  ): React.ReactNode => {
    // If count is 0, return empty string
    if (count === 0) {
      return '';
    }
    return (
      <span>
        {count}{' '}
        <span style={{ fontSize: '0.85em', opacity: 0.8 }}>({percent}%)</span>
      </span>
    );
  };

  // Format labels like statuses/interests to "Title case" (first letter capital, rest small)
  const formatLabel = (label: string): string => {
    if (!label) return '';
    return label
      .toLowerCase()
      .replace(/_/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Transform API data to component format
  const patientOverview = dashboardData?.patientOverview;
  const patientsVisitsData = patientOverview
    ? {
      patients: patientOverview.patients,
      categories: [
        {
          key: 'only-consultation',
          name: 'Only Consultation Invoice',
          new: patientOverview.onlyConsultation.new,
          newPercent: patientOverview.onlyConsultation.newPercentage,
          old: patientOverview.onlyConsultation.old,
          oldPercent: patientOverview.onlyConsultation.oldPercentage,
          total: patientOverview.onlyConsultation.total,
          totalPercent: patientOverview.onlyConsultation.totalPercentage,
          isHeader: true,
        },
        {
          key: 'only-consultation-non-zero',
          name: 'Non Zero Invoice',
          new: patientOverview.onlyConsultationNonZeroInvoice?.new ?? 0,
          newPercent: patientOverview.onlyConsultationNonZeroInvoice?.newPercentage ?? 0,
          old: patientOverview.onlyConsultationNonZeroInvoice?.old ?? 0,
          oldPercent: patientOverview.onlyConsultationNonZeroInvoice?.oldPercentage ?? 0,
          total: patientOverview.onlyConsultationNonZeroInvoice?.total ?? 0,
          totalPercent: patientOverview.onlyConsultationNonZeroInvoice?.totalPercentage ?? 0,
          isIndented: true,
        },
        {
          key: 'only-consultation-zero',
          name: 'Zero Invoice',
          new: patientOverview.onlyConsultationZeroInvoice?.new ?? 0,
          newPercent: patientOverview.onlyConsultationZeroInvoice?.newPercentage ?? 0,
          old: patientOverview.onlyConsultationZeroInvoice?.old ?? 0,
          oldPercent: patientOverview.onlyConsultationZeroInvoice?.oldPercentage ?? 0,
          total: patientOverview.onlyConsultationZeroInvoice?.total ?? 0,
          totalPercent: patientOverview.onlyConsultationZeroInvoice?.totalPercentage ?? 0,
          isIndented: true,
          hasBorderBottom: true,
        },
        {
          key: 'clinic-invoice',
          name: 'Treatment Invoice',
          new: patientOverview.clinicInvoiceCreated.new,
          newPercent: patientOverview.clinicInvoiceCreated.newPercentage,
          old: patientOverview.clinicInvoiceCreated.old,
          oldPercent: patientOverview.clinicInvoiceCreated.oldPercentage,
          total: patientOverview.clinicInvoiceCreated.total,
          totalPercent: patientOverview.clinicInvoiceCreated.totalPercentage,
          isHeader: true,
        },
        {
          key: 'clinic-invoice-non-zero',
          name: 'Non Zero Invoice',
          new: patientOverview.clinicInvoiceNonZeroInvoice?.new ?? 0,
          newPercent: patientOverview.clinicInvoiceNonZeroInvoice?.newPercentage ?? 0,
          old: patientOverview.clinicInvoiceNonZeroInvoice?.old ?? 0,
          oldPercent: patientOverview.clinicInvoiceNonZeroInvoice?.oldPercentage ?? 0,
          total: patientOverview.clinicInvoiceNonZeroInvoice?.total ?? 0,
          totalPercent: patientOverview.clinicInvoiceNonZeroInvoice?.totalPercentage ?? 0,
          isIndented: true,
        },
        {
          key: 'clinic-invoice-zero',
          name: 'Zero Invoice',
          new:
            patientOverview.clinicInvoiceZeroInvoice?.new ??
            patientOverview.zeroClinicInvoice?.new ??
            0,
          newPercent:
            patientOverview.clinicInvoiceZeroInvoice?.newPercentage ??
            patientOverview.zeroClinicInvoice?.newPercentage ??
            0,
          old:
            patientOverview.clinicInvoiceZeroInvoice?.old ??
            patientOverview.zeroClinicInvoice?.old ??
            0,
          oldPercent:
            patientOverview.clinicInvoiceZeroInvoice?.oldPercentage ??
            patientOverview.zeroClinicInvoice?.oldPercentage ??
            0,
          total:
            patientOverview.clinicInvoiceZeroInvoice?.total ??
            patientOverview.zeroClinicInvoice?.total ??
            0,
          totalPercent:
            patientOverview.clinicInvoiceZeroInvoice?.totalPercentage ??
            patientOverview.zeroClinicInvoice?.totalPercentage ??
            0,
          isIndented: true,
          hasBorderBottom: true,
        },
        {
          key: 'medical-invoice',
          name: 'Medical Invoice',
          new: patientOverview.medicineInvoiceCreated.new,
          newPercent: patientOverview.medicineInvoiceCreated.newPercentage,
          old: patientOverview.medicineInvoiceCreated.old,
          oldPercent: patientOverview.medicineInvoiceCreated.oldPercentage,
          total: patientOverview.medicineInvoiceCreated.total,
          totalPercent: patientOverview.medicineInvoiceCreated.totalPercentage,
          hasBorderBottom: true,
        },
        {
          key: 'treatment-taken',
          name: 'Treatment Taken',
          new: patientOverview.treatmentTaken.new,
          newPercent: patientOverview.treatmentTaken.newPercentage,
          old: patientOverview.treatmentTaken.old,
          oldPercent: patientOverview.treatmentTaken.oldPercentage,
          total: patientOverview.treatmentTaken.total,
          totalPercent: patientOverview.treatmentTaken.totalPercentage,
        },
      ],
    }
    : {
      patients: { new: 0, old: 0, total: 0 },
      categories: [],
    };

  // Transform leads overview data
  const leadsOverview = dashboardData?.leadsOverview;
  const leadModuleData = leadsOverview
    ? {
      leads: {
        google: {
          count: leadsOverview.totals.by_source.GOOGLE.count,
          percent: leadsOverview.totals.by_source.GOOGLE.percentage,
        },
        meta: {
          count: leadsOverview.totals.by_source.META.count,
          percent: leadsOverview.totals.by_source.META.percentage,
        },
        walkIn: {
          count: leadsOverview.totals.by_source.WALK_IN.count,
          percent: leadsOverview.totals.by_source.WALK_IN.percentage,
        },
        walkInReferral: {
          count: leadsOverview.totals.by_source.WALK_IN_REFERRAL.count,
          percent: leadsOverview.totals.by_source.WALK_IN_REFERRAL.percentage,
        },
        all: leadsOverview.totals.total_leads,
      },
      status: (() => {
        // Lead Module statuses (fixed order requested)
        const order = [
          'NEW',
          'FOLLOW_UP',
          'CONTACTED',
          'CLINIC_VISITED',
          'NOT_INTERESTED',
          'NO_RESPONSE',
          'INVALID',
          'LOST',
        ] as const;
        const orderIndex = new Map(order.map((k, i) => [k, i]));
        const label: Record<string, string> = {
          NEW: 'New',
          FOLLOW_UP: 'Followup',
          CONTACTED: 'Contacted',
          CLINIC_VISITED: 'Clinic Visited',
          NOT_INTERESTED: 'Not Interested',
          NO_RESPONSE: 'No Response',
          INVALID: 'Invalid',
          LOST: 'Lost',
        };

        return (leadsOverview.status || [])
          .filter((item: any) => orderIndex.has(item.status))
          .slice()
          .sort((a: any, b: any) => {
            return (
              (orderIndex.get(a.status) ?? 999) - (orderIndex.get(b.status) ?? 999)
            );
          })
          .map((item: any) => ({
            name: label[item.status] ?? String(item.status || '').replace(/_/g, ' '),
            google: { count: item.GOOGLE.count, percent: item.GOOGLE.percentage },
            meta: { count: item.META.count, percent: item.META.percentage },
            walkIn: {
              count: item.WALK_IN.count,
              percent: item.WALK_IN.percentage,
            },
            walkInReferral: {
              count: item.WALK_IN_REFERRAL.count,
              percent: item.WALK_IN_REFERRAL.percentage,
            },
            all: { count: item.ALL.count, percent: item.ALL.percentage },
          }));
      })(),
      interest: leadsOverview.interest.map(item => ({
        name: item.interest.replace(/_/g, ' '),
        google: { count: item.GOOGLE.count, percent: item.GOOGLE.percentage },
        meta: { count: item.META.count, percent: item.META.percentage },
        walkIn: {
          count: item.WALK_IN.count,
          percent: item.WALK_IN.percentage,
        },
        walkInReferral: {
          count: item.WALK_IN_REFERRAL.count,
          percent: item.WALK_IN_REFERRAL.percentage,
        },
        all: { count: item.ALL.count, percent: item.ALL.percentage },
      })),
    }
    : {
      leads: {
        google: { count: 0, percent: 0 },
        meta: { count: 0, percent: 0 },
        walkIn: { count: 0, percent: 0 },
        walkInReferral: { count: 0, percent: 0 },
        all: 0,
      },
      status: [],
      interest: [],
    };

  // Transform purchase overview data
  const purchaseOverview = dashboardData?.purchaseOverview;
  const purchaseModuleData = purchaseOverview
    ? [
      {
        invoiceType: 'Medical Purchase',
        finalAmount: purchaseOverview.medicalPurchase.final_amount,
        paidCash: purchaseOverview.medicalPurchase.paid_amount.cash,
        paidGPay: purchaseOverview.medicalPurchase.paid_amount.gpay,
        paidTotal: purchaseOverview.medicalPurchase.paid_amount.total,
        dueAmount: purchaseOverview.medicalPurchase.due_amount,
      },
      {
        invoiceType: 'Inside Purchase',
        finalAmount: purchaseOverview.insidePurchase.final_amount,
        paidCash: purchaseOverview.insidePurchase.paid_amount.cash,
        paidGPay: purchaseOverview.insidePurchase.paid_amount.gpay,
        paidTotal: purchaseOverview.insidePurchase.paid_amount.total,
        dueAmount: purchaseOverview.insidePurchase.due_amount,
      },
    ]
    : [];

  const purchaseTotal = purchaseOverview
    ? {
      finalAmount: purchaseOverview.total.final_amount,
      paidCash: purchaseOverview.total.paid_amount.cash,
      paidGPay: purchaseOverview.total.paid_amount.gpay,
      paidTotal: purchaseOverview.total.paid_amount.total,
      dueAmount: purchaseOverview.total.due_amount,
    }
    : {
      finalAmount: 0,
      paidCash: 0,
      paidGPay: 0,
      paidTotal: 0,
      dueAmount: 0,
    };

  // Transform sale overview data
  const saleOverview = dashboardData?.saleOverview;
  const saleModuleData = saleOverview
    ? [
      {
        invoiceType: 'Only Consultation Invoice',
        finalAmount: parseFloat((saleOverview.onlyConsultation as any).final_amount) || 0,
        discountAmount: parseFloat((saleOverview.onlyConsultation as any).discount_amount) || 0,
        total: parseFloat(saleOverview.onlyConsultation.total_amount) || 0,
        receivedCash:
          parseFloat(saleOverview.onlyConsultation.received_amount.cash) || 0,
        receivedGPay:
          parseFloat(saleOverview.onlyConsultation.received_amount.gpay) || 0,
        receivedTotal:
          parseFloat(saleOverview.onlyConsultation.received_amount.total) ||
          0,
        dueAmount: parseFloat(saleOverview.onlyConsultation.due_amount) || 0,
      },
      {
        invoiceType: 'Treatment Invoice',
        finalAmount: parseFloat((saleOverview.clinicInvoice as any).final_amount) || 0,
        discountAmount: parseFloat((saleOverview.clinicInvoice as any).discount_amount) || 0,
        total: parseFloat(saleOverview.clinicInvoice.total_amount) || 0,
        receivedCash:
          parseFloat(saleOverview.clinicInvoice.received_amount.cash) || 0,
        receivedGPay:
          parseFloat(saleOverview.clinicInvoice.received_amount.gpay) || 0,
        receivedTotal:
          parseFloat(saleOverview.clinicInvoice.received_amount.total) || 0,
        dueAmount: parseFloat(saleOverview.clinicInvoice.due_amount) || 0,
      },
      {
        invoiceType: 'Medical Invoice',
        finalAmount: parseFloat((saleOverview.medicalInvoice as any).final_amount) || 0,
        discountAmount: parseFloat((saleOverview.medicalInvoice as any).discount_amount) || 0,
        total: parseFloat(saleOverview.medicalInvoice.total_amount) || 0,
        receivedCash:
          parseFloat(saleOverview.medicalInvoice.received_amount.cash) || 0,
        receivedGPay:
          parseFloat(saleOverview.medicalInvoice.received_amount.gpay) || 0,
        receivedTotal:
          parseFloat(saleOverview.medicalInvoice.received_amount.total) || 0,
        dueAmount: parseFloat(saleOverview.medicalInvoice.due_amount) || 0,
      },
    ]
    : [];

  // Note: The API returns concatenated strings for totals, so we'll calculate them manually
  const saleTotal =
    saleModuleData.length > 0
      ? {
        finalAmount: saleModuleData.reduce((sum, item) => sum + item.finalAmount, 0),
        discountAmount: saleModuleData.reduce((sum, item) => sum + item.discountAmount, 0),
        total: saleModuleData.reduce((sum, item) => sum + item.total, 0),
        receivedCash: saleModuleData.reduce(
          (sum, item) => sum + item.receivedCash,
          0
        ),
        receivedGPay: saleModuleData.reduce(
          (sum, item) => sum + item.receivedGPay,
          0
        ),
        receivedTotal: saleModuleData.reduce(
          (sum, item) => sum + item.receivedTotal,
          0
        ),
        dueAmount: saleModuleData.reduce(
          (sum, item) => sum + item.dueAmount,
          0
        ),
      }
      : {
        finalAmount: 0,
        discountAmount: 0,
        total: 0,
        receivedCash: 0,
        receivedGPay: 0,
        receivedTotal: 0,
        dueAmount: 0,
      };

  // Transform top offerings data
  const topOfferingsData =
    dashboardData?.mostUsedOfferings?.offerings
      .slice(0, 10)
      .map((offering, index) => ({
        rank: index + 1,
        name: offering.offering_name,
        category: offering.offering_category,
        used: offering.usage_count,
      })) || [];

  // Transform accounting overview data
  const accountingOverview = dashboardData?.accountingOverview;
  const accountingData = accountingOverview
    ? {
      salesReceived: {
        gpay: accountingOverview.salesReceived.gpay,
        cash: accountingOverview.salesReceived.cash,
        total: accountingOverview.salesReceived.total,
      },
      oldInvoicesDue: {
        // Using "oldInvoicePayments" from API (payments received for old invoices)
        gpay: accountingOverview.oldInvoicePayments.gpay,
        cash: accountingOverview.oldInvoicePayments.cash,
        total: accountingOverview.oldInvoicePayments.total,
      },
      purchasePayments: {
        gpay: accountingOverview.PurchasePayments?.gpay || 0,
        cash: accountingOverview.PurchasePayments?.cash || 0,
        total: accountingOverview.PurchasePayments?.total || 0,
      },
      oldPurchasePayments: {
        gpay: accountingOverview.oldPurchasePayments?.gpay || 0,
        cash: accountingOverview.oldPurchasePayments?.cash || 0,
        total: accountingOverview.oldPurchasePayments?.total || 0,
      },
      expenses: [
        // Staff salaries
        ...(accountingOverview.staffSalaries.total > 0
          ? [
            {
              name: 'Staff salaries',
              gpay: accountingOverview.staffSalaries.gpay || null,
              cash: accountingOverview.staffSalaries.cash,
              total: accountingOverview.staffSalaries.total,
              grandTotal: accountingOverview.staffSalaries.total,
            },
          ]
          : []),
        // Rent & utilities
        ...(accountingOverview.rentUtilities.total > 0
          ? [
            {
              name: 'Rent & utilities',
              gpay: accountingOverview.rentUtilities.gpay || null,
              cash: accountingOverview.rentUtilities.cash,
              total: accountingOverview.rentUtilities.total,
              grandTotal: accountingOverview.rentUtilities.total,
            },
          ]
          : []),
        // Marketing expenses
        ...(accountingOverview.marketingExpenses.total > 0
          ? [
            {
              name: 'Marketing expenses',
              gpay: accountingOverview.marketingExpenses.gpay || null,
              cash: accountingOverview.marketingExpenses.cash,
              total: accountingOverview.marketingExpenses.total,
              grandTotal: accountingOverview.marketingExpenses.total,
            },
          ]
          : []),
        // Other expenses
        ...(accountingOverview.otherExpenses.total > 0
          ? [
            {
              name: 'Other expenses',
              gpay: accountingOverview.otherExpenses.gpay || null,
              cash: accountingOverview.otherExpenses.cash,
              total: accountingOverview.otherExpenses.total,
              grandTotal: accountingOverview.otherExpenses.total,
            },
          ]
          : []),
      ] as Array<{
        name: string;
        gpay: number | null;
        cash: number;
        total: number;
        grandTotal: number;
      }>,
      currentBalance: {
        // Using \"currentBalance\" from API as the current period balance summary
        gpay: accountingOverview.currentBalance.gpay,
        cash: accountingOverview.currentBalance.cash,
        total: accountingOverview.currentBalance.total,
      },
      patientsDue: {
        gpay: null,
        cash: accountingOverview.patientsDue.total,
        total: accountingOverview.patientsDue.total,
        grandTotal: accountingOverview.patientsDue.total,
      },
      purchasePendingPayment: {
        gpay: null,
        cash: accountingOverview.purchasePendingPayment.total,
        total: accountingOverview.purchasePendingPayment.total,
        grandTotal: accountingOverview.purchasePendingPayment.total,
      },
      finalBalance: {
        gpay: 0,
        cash:
          accountingOverview.currentBalance.cash -
          accountingOverview.patientsDue.total -
          accountingOverview.purchasePendingPayment.total,
        total:
          accountingOverview.currentBalance.total -
          accountingOverview.patientsDue.total -
          accountingOverview.purchasePendingPayment.total,
      },
    }
    : {
      // Fallback to previous behaviour if accountingOverview is not present
      salesReceived: {
        gpay: saleTotal.receivedGPay,
        cash: saleTotal.receivedCash,
        total: saleTotal.receivedTotal,
      },
      oldInvoicesDue: { gpay: 0, cash: 0, total: 0 },
      purchasePayments: { gpay: 0, cash: 0, total: 0 },
      oldPurchasePayments: { gpay: 0, cash: 0, total: 0 },
      expenses: [] as Array<{
        name: string;
        gpay: number | null;
        cash: number;
        total: number;
        grandTotal: number;
      }>,
      currentBalance: { gpay: 0, cash: 0, total: 0 },
      patientsDue: { gpay: null, cash: 0, total: 0, grandTotal: 0 },
      purchasePendingPayment: {
        gpay: null,
        cash: purchaseTotal.dueAmount,
        total: purchaseTotal.dueAmount,
        grandTotal: purchaseTotal.dueAmount,
      },
      finalBalance: { gpay: 0, cash: 0, total: 0 },
    };

  // Show loading state
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Error Loading Dashboard"
          description={error}
          type="error"
          showIcon
          action={
            <button
              onClick={fetchDashboardData}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#1890ff',
              }}
            >
              Retry
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div
      className="dashboard-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 40px)',
        minHeight: 'calc(100vh - 40px)',
        padding: 0,
        background: '#ffffff',
        overflow: 'hidden',
      }}
    >
      {/* Sticky Header Section - stays fixed while content scrolls */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          flexShrink: 0,
          padding: '24px 24px 16px',
          marginBottom: '8px',
          background: '#ffffff',
          borderBottom: '1px solid #f0f0f0',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 0 }}>
          <Col>
            <Title level={2} style={{ margin: 0, fontWeight: 'bold' }}>
              Report Overview
            </Title>
          </Col>
          <Col>
            <Space size="middle">
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                style={{ width: '280px' }}
                suffixIcon={<CalendarOutlined />}
              />
              <Select
                value={dateRangeOption}
                onChange={handleDateRangeOptionChange}
                style={{ width: '150px' }}
              >
                <Select.Option value="last_day">Last Day</Select.Option>
                <Select.Option value="last_week">Last Week</Select.Option>
                <Select.Option value="last_month">Last Month</Select.Option>
                <Select.Option value="last_two_months">
                  Last 2 Months
                </Select.Option>
                <Select.Option value="custom">Custom</Select.Option>
              </Select>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Scrollable content area */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '0 24px 24px',
        }}
      >
      {!hasUserSelectedDate ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 'calc(100vh - 200px)',
            textAlign: 'center',
          }}
        >
          <Text type="secondary" style={{ fontSize: '18px' }}>
            Please select date range to getting report
          </Text>
        </div>
      ) : (
        <>
          {/* Left: Patient, Purchase, Sale, Offering | Right: Lead Module */}
          <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
            {/* Left Column - Patient, Purchase, Sale, Offering */}
            <Col xs={24} lg={12}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Patients Visits Card */}
                <Card
                  style={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                  bodyStyle={{ padding: 0 }}
                >
                  {/* Card Header */}
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #13c2c2 0%, #36cfc9 100%)',
                      padding: '16px 20px',
                      borderRadius: '8px 8px 0 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <UsergroupAddOutlined
                      style={{ fontSize: '24px', color: '#fff' }}
                    />
                    <Text strong style={{ fontSize: '18px', color: '#fff' }}>
                      Patients Visits
                    </Text>
                  </div>

                  {/* Card Body */}
                  <div style={{ padding: 0, background: '#fff' }}>
                    <Table
                      className="patients-visits-table"
                      rowClassName={(record: any) => {
                        const classes: string[] = [];
                        if (record.hasBorderBottom) classes.push('patients-visits-border-bottom');
                        return classes.length > 0 ? classes.join(' ') : '';
                      }}
                      dataSource={[
                        {
                          key: 'patients',
                          category: 'Patient clinic visited',
                          new: patientsVisitsData.patients.new,
                          newPercent:
                            patientsVisitsData.patients.total > 0
                              ? Math.round(
                                (patientsVisitsData.patients.new /
                                  patientsVisitsData.patients.total) *
                                100
                              )
                              : 0,
                          old: patientsVisitsData.patients.old,
                          oldPercent:
                            patientsVisitsData.patients.total > 0
                              ? Math.round(
                                (patientsVisitsData.patients.old /
                                  patientsVisitsData.patients.total) *
                                100
                              )
                              : 0,
                          total: patientsVisitsData.patients.total,
                          totalPercent:
                            patientsVisitsData.patients.total > 0 ? 100 : 0,
                          isPatientsRow: true,
                          hasBorderBottom: true,
                        },
                        ...patientsVisitsData.categories.map((cat: any) => ({
                          key: cat.key ?? cat.name,
                          category: cat.name,
                          new: cat.new,
                          newPercent: cat.newPercent,
                          old: cat.old,
                          oldPercent: cat.oldPercent,
                          total: cat.total,
                          totalPercent: cat.totalPercent,
                          isIndented: !!cat.isIndented,
                          isHeader: !!cat.isHeader,
                          hasBorderBottom: !!cat.hasBorderBottom,
                        })),
                      ]}
                      columns={[
                        {
                          title: '',
                          dataIndex: 'category',
                          key: 'category',
                          render: (text: string, record: any) => (
                            <Text
                              strong={!!record.isPatientsRow || !!record.isHeader}
                              style={{
                                paddingLeft: record.isIndented ? 18 : 0,
                                display: 'inline-block',
                              }}
                            >
                              {text}
                            </Text>
                          ),
                        },
                        {
                          title: 'New',
                          dataIndex: 'new',
                          key: 'new',
                          align: 'center',
                          render: (_: any, record: any) => {
                            if (
                              typeof record.new === 'number' &&
                              typeof record.newPercent === 'number'
                            ) {
                              return formatWithPercent(record.new, record.newPercent);
                            }
                            return record.new;
                          },
                        },
                        {
                          title: 'Old',
                          dataIndex: 'old',
                          key: 'old',
                          align: 'center',
                          render: (_: any, record: any) => {
                            if (
                              typeof record.old === 'number' &&
                              typeof record.oldPercent === 'number'
                            ) {
                              return formatWithPercent(record.old, record.oldPercent);
                            }
                            return record.old;
                          },
                        },
                        {
                          title: 'Total',
                          dataIndex: 'total',
                          key: 'total',
                          align: 'center',
                          render: (_: any, record: any) => {
                            if (
                              typeof record.total === 'number' &&
                              typeof record.totalPercent === 'number'
                            ) {
                              // For the main Patients row, show only the total count (no percentage)
                              if (record.category === 'Patients') {
                                return record.total;
                              }
                              return formatWithPercent(
                                record.total,
                                record.totalPercent
                              );
                            }
                            return record.total;
                          },
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
                  </div>
                </Card>

                {/* Sale Module */}
                <Card
                  style={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                  bodyStyle={{ padding: 0 }}
                >
                  {/* Card Header */}
                  <div
                    style={{
                      background:
                        'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                      padding: '16px 20px',
                      borderRadius: '8px 8px 0 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <DollarOutlined style={{ fontSize: '24px', color: '#fff' }} />
                    <Text strong style={{ fontSize: '18px', color: '#fff' }}>
                      Sale Module
                    </Text>
                  </div>

                  {/* Card Body */}
                  <div style={{ padding: 0, background: '#fff' }}>
                    {!hasUserSelectedDate ? (
                      <div style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                          Please select a date range from the filter above to view Sale Module data
                        </Text>
                      </div>
                    ) : (
                      <Table
                        className="sale-module-table"
                        dataSource={[
                          ...saleModuleData.map(item => ({
                            key: item.invoiceType,
                            invoiceType: item.invoiceType,
                            finalAmount: formatCurrency(item.finalAmount),
                            discountAmount: formatCurrency(item.discountAmount),
                            total: formatCurrency(item.total),
                            receivedCash: formatCurrency(item.receivedCash),
                            receivedGPay: formatCurrency(item.receivedGPay),
                            receivedTotal: formatCurrency(item.receivedTotal),
                            dueAmount: formatCurrency(item.dueAmount),
                          })),
                          {
                            key: 'total',
                            invoiceType: 'Total',
                            finalAmount: formatCurrency(saleTotal.finalAmount),
                            discountAmount: formatCurrency(saleTotal.discountAmount),
                            total: formatCurrency(saleTotal.total),
                            receivedCash: formatCurrency(saleTotal.receivedCash),
                            receivedGPay: formatCurrency(saleTotal.receivedGPay),
                            receivedTotal: formatCurrency(saleTotal.receivedTotal),
                            dueAmount: formatCurrency(saleTotal.dueAmount),
                          },
                        ]}
                        rowClassName={record =>
                          record.invoiceType === 'Total' ? 'sale-total-row' : ''
                        }
                        columns={[
                          {
                            title: <Text style={{ fontSize: '12px' }}>Invoice Type</Text>,
                            dataIndex: 'invoiceType',
                            key: 'invoiceType',
                            render: (text: string) => (
                              <Text strong={text === 'Total'} style={{ fontSize: '11px' }}>{text}</Text>
                            ),
                          },
                          {
                            title: <Text style={{ fontSize: '12px' }}>Total Amount</Text>,
                            dataIndex: 'finalAmount',
                            key: 'finalAmount',
                            align: 'right',
                            render: (text: string, record: any) => (
                              <Text strong={record.invoiceType === 'Total'} style={{ fontSize: '11px' }}>
                                {text}
                              </Text>
                            ),
                          },
                          {
                            title: (
                              <Text strong style={{ color: '#fa8c16', fontSize: '12px' }}>
                                Discount
                              </Text>
                            ),
                            dataIndex: 'discountAmount',
                            key: 'discountAmount',
                            align: 'right',
                            render: (text: string, record: any) => (
                              <Text
                                strong={record.invoiceType === 'Total'}
                                style={{ color: '#fa8c16', fontSize: '11px' }}
                              >
                                {text}
                              </Text>
                            ),
                          },
                          {
                            title: <Text style={{ fontSize: '12px' }}>Final Amount</Text>,
                            dataIndex: 'total',
                            key: 'total',
                            align: 'right',
                            render: (text: string, record: any) => (
                              <Text strong={record.invoiceType === 'Total'} style={{ fontSize: '11px' }}>
                                {text}
                              </Text>
                            ),
                          },
                          {
                            title: (
                              <Text strong style={{ color: '#389e0d', fontSize: '12px' }}>
                                Received Amount
                              </Text>
                            ),
                            children: [
                              {
                                title: (
                                  <Text strong style={{ color: '#389e0d', fontSize: '12px' }}>
                                    Cash
                                  </Text>
                                ),
                                dataIndex: 'receivedCash',
                                key: 'receivedCash',
                                align: 'right',
                                render: (text: string, record: any) => (
                                  <Text
                                    strong={record.invoiceType === 'Total'}
                                    style={{ color: '#389e0d', fontSize: '11px' }}
                                  >
                                    {text}
                                  </Text>
                                ),
                              },
                              {
                                title: (
                                  <Text strong style={{ color: '#1677ff', fontSize: '12px' }}>
                                    GPay
                                  </Text>
                                ),
                                dataIndex: 'receivedGPay',
                                key: 'receivedGPay',
                                align: 'right',
                                render: (text: string, record: any) => (
                                  <Text
                                    strong={record.invoiceType === 'Total'}
                                    style={{ color: '#1677ff', fontSize: '11px' }}
                                  >
                                    {text}
                                  </Text>
                                ),
                              },
                              {
                                title: (
                                  <Text strong style={{ color: '#389e0d', fontSize: '12px' }}>
                                    Total
                                  </Text>
                                ),
                                dataIndex: 'receivedTotal',
                                key: 'receivedTotal',
                                align: 'right',
                                render: (text: string, record: any) => (
                                  <Text
                                    strong={record.invoiceType === 'Total'}
                                    style={{ color: '#389e0d', fontSize: '11px' }}
                                  >
                                    {text}
                                  </Text>
                                ),
                              },
                            ],
                          },
                          {
                            title: (
                              <Text strong style={{ color: '#ff4d4f', fontSize: '12px' }}>
                                Due Amount
                              </Text>
                            ),
                            dataIndex: 'dueAmount',
                            key: 'dueAmount',
                            align: 'right',
                            render: (text: string, record: any) => (
                              <Text
                                strong={record.invoiceType === 'Total'}
                                style={{ color: '#ff4d4f', fontSize: '11px' }}
                              >
                                {text}
                              </Text>
                            ),
                          },
                        ]}
                        pagination={false}
                        size="small"
                      />
                    )
                    }
                  </div>
                </Card>

                {/* Purchase Module */}
                <Card
                  style={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                  bodyStyle={{ padding: 0 }}
                >
                  {/* Card Header */}
                  <div
                    style={{
                      background:
                        'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
                      padding: '16px 20px',
                      borderRadius: '8px 8px 0 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <ShoppingCartOutlined
                      style={{ fontSize: '24px', color: '#fff' }}
                    />
                    <Text strong style={{ fontSize: '18px', color: '#fff' }}>
                      Purchase Module
                    </Text>
                  </div>

                  {/* Card Body */}
                  <div style={{ padding: 0, background: '#fff' }}>
                    <Table
                      className="purchase-module-table"
                      dataSource={[
                        ...purchaseModuleData.map(item => ({
                          key: item.invoiceType,
                          invoiceType: item.invoiceType,
                          finalAmount: formatCurrency(item.finalAmount),
                          paidCash: formatCurrency(item.paidCash),
                          paidGPay: formatCurrency(item.paidGPay),
                          paidTotal: formatCurrency(item.paidTotal),
                          dueAmount: formatCurrency(item.dueAmount),
                        })),
                        {
                          key: 'total',
                          invoiceType: 'Total',
                          finalAmount: formatCurrency(purchaseTotal.finalAmount),
                          paidCash: formatCurrency(purchaseTotal.paidCash),
                          paidGPay: formatCurrency(purchaseTotal.paidGPay),
                          paidTotal: formatCurrency(purchaseTotal.paidTotal),
                          dueAmount: formatCurrency(purchaseTotal.dueAmount),
                        },
                      ]}
                      rowClassName={record =>
                        record.invoiceType === 'Total' ? 'purchase-total-row' : ''
                      }
                      columns={[
                        {
                          title: <Text style={{ fontSize: '12px' }}>Invoice Type</Text>,
                          dataIndex: 'invoiceType',
                          key: 'invoiceType',
                          render: (text: string) => (
                            <Text strong={text === 'Total'} style={{ fontSize: '11px' }}>{text}</Text>
                          ),
                        },
                        {
                          title: <Text style={{ fontSize: '12px' }}>Amount</Text>,
                          dataIndex: 'finalAmount',
                          key: 'finalAmount',
                          align: 'right',
                          render: (text: string, record: any) => (
                            <Text strong={record.invoiceType === 'Total'} style={{ fontSize: '11px' }}>
                              {text}
                            </Text>
                          ),
                        },
                        {
                          title: (
                            <Text strong style={{ color: '#389e0d', fontSize: '12px' }}>
                              Paid Amount
                            </Text>
                          ),
                          children: [
                            {
                              title: (
                                <Text strong style={{ color: '#389e0d', fontSize: '12px' }}>
                                  Cash
                                </Text>
                              ),
                              dataIndex: 'paidCash',
                              key: 'paidCash',
                              align: 'right',
                              render: (text: string, record: any) => (
                                <Text
                                  strong={record.invoiceType === 'Total'}
                                  style={{ color: '#389e0d', fontSize: '11px' }}
                                >
                                  {text}
                                </Text>
                              ),
                            },
                            {
                              title: (
                                <Text strong style={{ color: '#1677ff', fontSize: '12px' }}>
                                  GPay
                                </Text>
                              ),
                              dataIndex: 'paidGPay',
                              key: 'paidGPay',
                              align: 'right',
                              render: (text: string, record: any) => (
                                <Text
                                  strong={record.invoiceType === 'Total'}
                                  style={{ color: '#1677ff', fontSize: '11px' }}
                                >
                                  {text}
                                </Text>
                              ),
                            },
                            {
                              title: (
                                <Text strong style={{ color: '#389e0d', fontSize: '12px' }}>
                                  Total
                                </Text>
                              ),
                              dataIndex: 'paidTotal',
                              key: 'paidTotal',
                              align: 'right',
                              render: (text: string, record: any) => (
                                <Text
                                  strong={record.invoiceType === 'Total'}
                                  style={{ color: '#389e0d', fontSize: '11px' }}
                                >
                                  {text}
                                </Text>
                              ),
                            },
                          ],
                        },
                        {
                          title: (
                            <Text strong style={{ color: '#ff4d4f', fontSize: '12px' }}>
                              Due Amount
                            </Text>
                          ),
                          dataIndex: 'dueAmount',
                          key: 'dueAmount',
                          align: 'right',
                          render: (text: string, record: any) => (
                            <Text
                              strong={record.invoiceType === 'Total'}
                              style={{ color: '#ff4d4f', fontSize: '11px' }}
                            >
                              {text}
                            </Text>
                          ),
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
                  </div>
                </Card>

                {/* Accounting Card */}
                <Card
                  style={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                  bodyStyle={{ padding: 0 }}
                >
                  {/* Card Header */}
                  <div
                    style={{
                      background:
                        'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                      padding: '16px 20px',
                      borderRadius: '8px 8px 0 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <CalculatorOutlined
                      style={{ fontSize: '24px', color: '#fff' }}
                    />
                    <Text strong style={{ fontSize: '18px', color: '#fff' }}>
                      Accounting
                    </Text>
                  </div>

                  {/* Card Body */}
                  <div style={{ padding: 0, background: '#fff' }}>
                    {!hasUserSelectedDate ? (
                      <div style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                          Please select a date range from the filter above to view Accounting data
                        </Text>
                      </div>
                    ) : (
                      <Table
                        className="sale-module-table"
                        rowClassName={(record: any) => {
                          const classes: string[] = [];
                          if (record.hasBorderTop) classes.push('accounting-border-top');
                          if (record.hasBorderBottom) classes.push('accounting-border-bottom');
                          return classes.length > 0 ? classes.join(' ') : '';
                        }}
                        dataSource={[
                          {
                            key: 'sales-received',
                            description: 'Sales received amount',
                            descriptionType: 'from-to',
                            startDate: dateRange && dateRange[0] ? dayjs(dateRange[0]).format('D/M/YYYY') : '1 Dec 2025',
                            endDate: dateRange && dateRange[1] ? dayjs(dateRange[1]).format('D/M/YYYY') : '31 Dec 2025',
                            gpay: formatCurrency(accountingData.salesReceived.gpay),
                            cash: formatCurrency(accountingData.salesReceived.cash),
                            total: formatCurrency(accountingData.salesReceived.total),
                            isBold: false,
                            isRed: false,
                            isGreen: true,
                          },
                          {
                            key: 'old-invoices',
                            description: 'Sales received amount',
                            descriptionType: 'old-due-before',
                            startDate: dateRange && dateRange[0] ? dayjs(dateRange[0]).format('D/M/YYYY') : '1 Dec 2025',
                            gpay: formatCurrency(accountingData.oldInvoicesDue.gpay),
                            cash: formatCurrency(accountingData.oldInvoicesDue.cash),
                            total: formatCurrency(
                              accountingData.oldInvoicesDue.total
                            ),
                            isBold: false,
                            isRed: false,
                            isGreen: true,
                            hasBorderBottom: true,
                          },
                          {
                            key: 'purchase-payments',
                            description: 'Purchase payments',
                            descriptionType: 'from-to',
                            startDate: dateRange && dateRange[0] ? dayjs(dateRange[0]).format('D/M/YYYY') : '1 Dec 2025',
                            endDate: dateRange && dateRange[1] ? dayjs(dateRange[1]).format('D/M/YYYY') : '31 Dec 2025',
                            gpay: formatCurrency(accountingData.purchasePayments.gpay),
                            cash: formatCurrency(accountingData.purchasePayments.cash),
                            total: formatCurrency(
                              accountingData.purchasePayments.total
                            ),
                            isBold: false,
                            isRed: false,
                            isGreen: false,
                            isTotalRed: true,
                            hasBorderTop: false,
                          },
                          {
                            key: 'old-purchase-payments',
                            description: 'Purchase payments',
                            descriptionType: 'old-pending-before',
                            startDate: dateRange && dateRange[0] ? dayjs(dateRange[0]).format('D/M/YYYY') : '1 Dec 2025',
                            gpay: formatCurrency(accountingData.oldPurchasePayments.gpay),
                            cash: formatCurrency(accountingData.oldPurchasePayments.cash),
                            total: formatCurrency(
                              accountingData.oldPurchasePayments.total
                            ),
                            isBold: false,
                            isRed: false,
                            isGreen: false,
                            isTotalRed: true,
                          },
                          ...(accountingData.expenses || []).map(
                            (expense, index) => ({
                              key: `expense-${index}`,
                              description: expense.name,
                              gpay: expense.gpay ? formatCurrency(expense.gpay) : '',
                              cash: formatCurrency(expense.cash),
                              total: formatCurrency(expense.grandTotal),
                              isBold: false,
                              isRed: true,
                              isGreen: false,
                              isIndented: false,
                            })
                          ),
                          {
                            key: 'current-balance',
                            description: 'Current Balance (Before Due/Pending Payments)',
                            gpay: formatCurrency(accountingData.currentBalance.gpay),
                            cash: formatCurrency(accountingData.currentBalance.cash),
                            total: formatCurrency(
                              accountingData.currentBalance.total
                            ),
                            isBold: true,
                            isRed: false,
                            isGreen: true,
                            hasBorderBottom: true,
                            hasBorderTop: true,
                          },
                          {
                            key: 'patients-due',
                            description: 'Sales due amount',
                            descriptionType: 'till',
                            endDate: dateRange && dateRange[1] ? dayjs(dateRange[1]).format('D/M/YYYY') : '31 Dec 2025',
                            gpay: accountingData.patientsDue.gpay
                              ? formatCurrency(accountingData.patientsDue.gpay)
                              : '',
                            // Hide cash value for Patients due row
                            cash: '',
                            total: formatCurrency(
                              accountingData.patientsDue.grandTotal
                            ),
                            isBold: false,
                            // Show Patients due values in green
                            isRed: false,
                            isGreen: true,
                          },
                          {
                            key: 'purchase-pending',
                            description: 'Purchase pending payment',
                            descriptionType: 'till',
                            endDate: dateRange && dateRange[1] ? dayjs(dateRange[1]).format('D/M/YYYY') : '31 Dec 2025',
                            gpay: accountingData.purchasePendingPayment.gpay
                              ? formatCurrency(
                                accountingData.purchasePendingPayment.gpay
                              )
                              : '',
                            // Hide cash value for Purchase pending payment row
                            cash: '',
                            total: formatCurrency(
                              accountingData.purchasePendingPayment.grandTotal
                            ),
                            isBold: false,
                            isRed: true,
                            isGreen: false,
                            hasBorderBottom: true,
                          },
                          {
                            key: 'final-balance',
                            description: 'Final Balance',
                            gpay: formatCurrency(accountingData.currentBalance.gpay),
                            cash: formatCurrency(accountingData.currentBalance.cash),
                            total: formatCurrency(
                              accountingData.currentBalance.total +
                              accountingData.patientsDue.total -
                              accountingData.purchasePendingPayment.total
                            ),
                            isBold: true,
                            isRed: false,
                            isGreen: true,
                          },
                        ]}
                        columns={[
                          {
                            title: '',
                            dataIndex: 'description',
                            key: 'description',
                            render: (text: string, record: any) => {
                              const getLabel = () => {
                                switch (record.key) {
                                  case 'sales-received':
                                  case 'old-invoices':
                                    return 'Received Amount';
                                  case 'purchase-payments':
                                  case 'old-purchase-payments':
                                    return 'Paid';
                                  default:
                                    return text;
                                }
                              };

                              const label = getLabel();

                              // Keep all text same size/color (no small grey subtext)
                              let fullText = label;
                              if (
                                record.descriptionType === 'from-to' &&
                                record.startDate &&
                                record.endDate
                              ) {
                                if (record.key === 'purchase-payments') {
                                  fullText = `${record.startDate} to ${record.endDate} Purchase Payment | Paid`;
                                } else {
                                  fullText = `${record.startDate} to ${record.endDate} | ${label}`;
                                }
                              } else if (
                                record.descriptionType === 'old-due-before' &&
                                record.startDate
                              ) {
                                fullText = `Before ${record.startDate} Due | ${label}`;
                              } else if (
                                record.descriptionType === 'old-pending-before' &&
                                record.startDate
                              ) {
                                fullText = `Before ${record.startDate} Pending Purchase Payment | ${label}`;
                              } else if (record.descriptionType === 'till' && record.endDate) {
                                fullText = `${label} (Till ${record.endDate})`;
                              }

                              return (
                                <Text
                                  strong={record.isBold}
                                  style={{
                                    paddingLeft: record.isIndented ? '24px' : '0',
                                    fontWeight: record.isBold ? 700 : 500,
                                    fontSize: record.key === 'current-balance' ? 14 : 12,
                                    color: '#262626',
                                  }}
                                >
                                  {fullText}
                                </Text>
                              );
                            },
                          },
                          {
                            title: (
                              <Text strong style={{ color: '#389e0d' }}>
                                Cash
                              </Text>
                            ),
                            dataIndex: 'cash',
                            key: 'cash',
                            align: 'right',
                            render: (text: string, record: any) => (
                              <Text
                                strong={record.isBold}
                                style={{
                                  fontSize: record.key === 'current-balance' ? 13 : 11,
                                  // Cash column: green by default, red for negative/dues
                                  color: record.isRed ? '#ff4d4f' : '#389e0d',
                                }}
                              >
                                {text}
                              </Text>
                            ),
                          },
                          {
                            title: (
                              <Text strong style={{ color: '#1677ff' }}>
                                GPay
                              </Text>
                            ),
                            dataIndex: 'gpay',
                            key: 'gpay',
                            align: 'right',
                            render: (text: string, record: any) => (
                              <Text
                                strong={record.isBold}
                                style={{
                                  fontSize: record.key === 'current-balance' ? 13 : 11,
                                  // GPay column: blue by default, red for negative/dues
                                  color: record.isRed ? '#ff4d4f' : '#1677ff',
                                }}
                              >
                                {text}
                              </Text>
                            ),
                          },
                          {
                            title: (
                              <Text strong style={{ color: '#389e0d' }}>
                                Total
                              </Text>
                            ),
                            dataIndex: 'total',
                            key: 'total',
                            align: 'right',
                            render: (text: string, record: any) => {
                              if (!text) return '';
                              let color = '#389e0d'; // default green for Total

                              if (record.isRed || record.isTotalRed) {
                                color = '#ff4d4f';
                              }

                              const sign = (record.isRed || record.isTotalRed) ? '-' : (record.isGreen ? '+' : '');

                              return (
                                <Text
                                  strong={record.isBold}
                                  style={{
                                    fontSize: record.key === 'current-balance' ? 13 : 11,
                                    color,
                                  }}
                                >
                                  {sign}{text}
                                </Text>
                              );
                            },
                          },
                        ]}
                        pagination={false}
                        size="small"
                      />
                    )}
                  </div>
                </Card>
              </Space>
            </Col>

            {/* Right Column - Lead Module */}
            <Col xs={24} lg={12}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
             {/* Lead Module Card */}
              <Card
                style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
                bodyStyle={{ padding: 0 }}
              >
                {/* Card Header */}
                <div
                  style={{
                    background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
                    padding: '16px 20px',
                    borderRadius: '8px 8px 0 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <LineChartOutlined style={{ fontSize: '24px', color: '#fff' }} />
                  <Text strong style={{ fontSize: '18px', color: '#fff' }}>
                    Lead Module
                  </Text>
                </div>

                {/* Card Body */}
                <div style={{ padding: 0, background: '#fff' }}>
                  {/* Leads Summary */}
                  <Table
                    className="lead-module-leads-table"
                    dataSource={[
                      {
                        key: 'leads',
                        category: 'Leads',
                        google: formatWithPercent(
                          leadModuleData.leads.google.count,
                          leadModuleData.leads.google.percent
                        ),
                        meta: formatWithPercent(
                          leadModuleData.leads.meta.count,
                          leadModuleData.leads.meta.percent
                        ),
                        walkIn: formatWithPercent(
                          leadModuleData.leads.walkIn.count,
                          leadModuleData.leads.walkIn.percent
                        ),
                        walkInReferral: formatWithPercent(
                          leadModuleData.leads.walkInReferral.count,
                          leadModuleData.leads.walkInReferral.percent
                        ),
                        all: leadModuleData.leads.all,
                        isSectionHeader: false,
                      },
                    ]}
                    columns={[
                      {
                        title: '',
                        dataIndex: 'category',
                        key: 'category',
                        width: 140,
                        fixed: 'left' as const,
                        render: (text: string) => <Text strong>{text}</Text>,
                      },
                      {
                        title: 'Google',
                        dataIndex: 'google',
                        key: 'google',
                        align: 'center',
                        width: 100,
                        render: (text: React.ReactNode) => text,
                      },
                      {
                        title: 'Meta',
                        dataIndex: 'meta',
                        key: 'meta',
                        align: 'center',
                        width: 100,
                        render: (text: React.ReactNode) => text,
                      },
                      {
                        title: 'Walk-in',
                        dataIndex: 'walkIn',
                        key: 'walkIn',
                        align: 'center',
                        width: 90,
                        render: (text: React.ReactNode) => text,
                      },
                      {
                        title: 'Walk-in (Referral)',
                        dataIndex: 'walkInReferral',
                        key: 'walkInReferral',
                        align: 'center',
                        width: 140,
                        render: (text: React.ReactNode) => text,
                      },
                      {
                        title: 'All',
                        dataIndex: 'all',
                        key: 'all',
                        align: 'center',
                        width: 80,
                        render: (text: number) => <Text strong>{text}</Text>,
                      },
                    ]}
                    pagination={false}
                    size="small"
                    style={{ marginBottom: 0 }}
                    scroll={{ x: 'max-content' }}
                  />

                  {/* Status Section */}
                  <Table
                    className="lead-module-status-table"
                    dataSource={[
                      {
                        key: 'status-header',
                        category: 'Status',
                        google: '',
                        meta: '',
                        walkIn: '',
                        walkInReferral: '',
                        all: '',
                        isSectionHeader: true,
                      },
                      ...leadModuleData.status.map(status => ({
                        key: status.name,
                        category: status.name,
                        google: formatWithPercent(
                          status.google.count,
                          status.google.percent
                        ),
                        meta: formatWithPercent(
                          status.meta.count,
                          status.meta.percent
                        ),
                        walkIn: formatWithPercent(
                          status.walkIn.count,
                          status.walkIn.percent
                        ),
                        walkInReferral: formatWithPercent(
                          status.walkInReferral.count,
                          status.walkInReferral.percent
                        ),
                        all: formatWithPercent(
                          status.all.count,
                          status.all.percent
                        ),
                        isSectionHeader: false,
                      })),
                    ]}
                    columns={[
                      {
                        title: '',
                        dataIndex: 'category',
                        key: 'category',
                        width: 140,
                        fixed: 'left' as const,
                        render: (text: string, record: any) => {
                          if (record.isSectionHeader) {
                            return (
                              <Text
                                strong
                                style={{ fontSize: '16px', color: '#722ed1' }}
                              >
                                {text}
                              </Text>
                            );
                          }
                          return <Text>{formatLabel(text)}</Text>;
                        },
                      },
                      {
                        title: '',
                        dataIndex: 'google',
                        key: 'google',
                        align: 'center',
                        width: 100,
                        render: (text: React.ReactNode, record: any) => {
                          if (record.isSectionHeader) return '';
                          return text;
                        },
                      },
                      {
                        title: '',
                        dataIndex: 'meta',
                        key: 'meta',
                        align: 'center',
                        width: 100,
                        render: (text: React.ReactNode, record: any) => {
                          if (record.isSectionHeader) return '';
                          return text;
                        },
                      },
                      {
                        title: '',
                        dataIndex: 'walkIn',
                        key: 'walkIn',
                        align: 'center',
                        width: 90,
                        render: (text: React.ReactNode, record: any) => {
                          if (record.isSectionHeader) return '';
                          return text;
                        },
                      },
                      {
                        title: '',
                        dataIndex: 'walkInReferral',
                        key: 'walkInReferral',
                        align: 'center',
                        width: 140,
                        render: (text: React.ReactNode, record: any) => {
                          if (record.isSectionHeader) return '';
                          return text;
                        },
                      },
                      {
                        title: '',
                        dataIndex: 'all',
                        key: 'all',
                        align: 'center',
                        width: 80,
                        render: (text: React.ReactNode, record: any) => {
                          if (record.isSectionHeader) return '';
                          return text;
                        },
                      },
                    ]}
                    pagination={false}
                    size="small"
                    showHeader={false}
                    scroll={{ x: 'max-content' }}
                  />

                  {/* Interest Section */}
                  <Table
                    className="lead-module-interest-table"
                    dataSource={[
                      {
                        key: 'interest-header',
                        category: 'Interest',
                        google: '',
                        meta: '',
                        walkIn: '',
                        walkInReferral: '',
                        all: '',
                        isSectionHeader: true,
                      },
                      ...leadModuleData.interest.map(interest => ({
                        key: interest.name,
                        category: interest.name,
                        google: formatWithPercent(
                          interest.google.count,
                          interest.google.percent
                        ),
                        meta: formatWithPercent(
                          interest.meta.count,
                          interest.meta.percent
                        ),
                        walkIn: formatWithPercent(
                          interest.walkIn.count,
                          interest.walkIn.percent
                        ),
                        walkInReferral: formatWithPercent(
                          interest.walkInReferral.count,
                          interest.walkInReferral.percent
                        ),
                        all: formatWithPercent(
                          interest.all.count,
                          interest.all.percent
                        ),
                        isSectionHeader: false,
                      })),
                    ]}
                    columns={[
                      {
                        title: '',
                        dataIndex: 'category',
                        key: 'category',
                        width: 140,
                        fixed: 'left' as const,
                        render: (text: string, record: any) => {
                          if (record.isSectionHeader) {
                            return (
                              <Text
                                strong
                                style={{ fontSize: '16px', color: '#722ed1' }}
                              >
                                {text}
                              </Text>
                            );
                          }
                          return <Text>{formatLabel(text)}</Text>;
                        },
                      },
                      {
                        title: '',
                        dataIndex: 'google',
                        key: 'google',
                        align: 'center',
                        width: 100,
                        render: (text: React.ReactNode, record: any) => {
                          if (record.isSectionHeader) return '';
                          return text;
                        },
                      },
                      {
                        title: '',
                        dataIndex: 'meta',
                        key: 'meta',
                        align: 'center',
                        width: 100,
                        render: (text: React.ReactNode, record: any) => {
                          if (record.isSectionHeader) return '';
                          return text;
                        },
                      },
                      {
                        title: '',
                        dataIndex: 'walkIn',
                        key: 'walkIn',
                        align: 'center',
                        width: 90,
                        render: (text: React.ReactNode, record: any) => {
                          if (record.isSectionHeader) return '';
                          return text;
                        },
                      },
                      {
                        title: '',
                        dataIndex: 'walkInReferral',
                        key: 'walkInReferral',
                        align: 'center',
                        width: 140,
                        render: (text: React.ReactNode, record: any) => {
                          if (record.isSectionHeader) return '';
                          return text;
                        },
                      },
                      {
                        title: '',
                        dataIndex: 'all',
                        key: 'all',
                        align: 'center',
                        width: 80,
                        render: (text: React.ReactNode, record: any) => {
                          if (record.isSectionHeader) return '';
                          return text;
                        },
                      },
                    ]}
                    pagination={false}
                    size="small"
                    showHeader={false}
                    scroll={{ x: 'max-content' }}
                  />
                </div>
              </Card>

              {/* Top 10 Most Used Offerings Card */}
              <Card
                style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
                bodyStyle={{ padding: 0 }}
              >
                {/* Card Header */}
                <div
                  style={{
                    background:
                      'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
                    padding: '16px 20px',
                    borderRadius: '8px 8px 0 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <TrophyOutlined style={{ fontSize: '24px', color: '#fff' }} />
                  <Text strong style={{ fontSize: '18px', color: '#fff' }}>
                    Top 10 Most Used Offerings
                  </Text>
                </div>

                {/* Card Body */}
                <div style={{ padding: 0, background: '#fff' }}>
                  {!hasUserSelectedDate ? (
                    <div style={{ padding: '16px 20px', textAlign: 'center' }}>
                      <Text type="secondary" style={{ fontSize: '14px' }}>
                        Please select a date range from the filter above to view Top 10 Most Used Offerings
                      </Text>
                    </div>
                  ) : (
                    <Table
                      dataSource={topOfferingsData.map(item => ({
                        key: item.rank,
                        rank: item.rank,
                        name: item.name,
                        category: item.category,
                        used: item.used,
                      }))}
                      columns={[
                        {
                          title: '#',
                          dataIndex: 'rank',
                          key: 'rank',
                          width: 60,
                          align: 'center',
                        },
                        {
                          title: 'Offering Name',
                          dataIndex: 'name',
                          key: 'name',
                        },
                        {
                          title: 'Category',
                          dataIndex: 'category',
                          key: 'category',
                          render: (text: string) => {
                            const config = getCategoryConfig(text);
                            return (
                              <Tag color={config.color} style={{ fontWeight: 500 }}>
                                {config.text}
                              </Tag>
                            );
                          },
                          width: 100,
                          align: 'center',
                        },
                        {
                          title: 'Used',
                          dataIndex: 'used',
                          key: 'used',
                          align: 'right',
                          render: (text: number) => (
                            <Text
                              strong
                              style={{ color: '#1890ff', paddingRight: '16px' }}
                            >
                              {text}
                            </Text>
                          ),
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
                  )}
                </div>
              </Card>
            </Space>
            </Col>
          </Row>

          {/* Patient Invoice Details Row */}
          <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
            <Col xs={24}>
              <Card
                style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
                bodyStyle={{ padding: 0 }}
              >
                <div
                  className="patient-invoice-header"
                  style={{
                    background: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)',
                    padding: '16px 20px',
                    borderRadius: '8px 8px 0 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                  >
                    <DollarOutlined style={{ fontSize: '24px', color: '#fff' }} />
                    <Text strong style={{ fontSize: '18px', color: '#fff' }}>
                      Patients with Clinic Invoice Details
                    </Text>
                  </div>
                </div>

                <div style={{ padding: 0, background: '#fff', overflowX: 'auto' }}>
                  {!hasUserSelectedDate ? (
                    <div style={{ padding: '16px 20px', textAlign: 'center' }}>
                      <Text type="secondary" style={{ fontSize: '14px' }}>
                        Please select a date range from the filter above to view Patients with Clinic Invoice Details
                      </Text>
                    </div>
                  ) : transactionError ? (
                    <div style={{ padding: '16px 20px' }}>
                      <Alert
                        message="Error Loading Transaction Report"
                        description={transactionError}
                        type="error"
                        showIcon
                        action={
                          <button
                            onClick={fetchTransactionReport}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#1890ff',
                            }}
                          >
                            Retry
                          </button>
                        }
                      />
                    </div>
                  ) : transactionLoading ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '200px',
                      }}
                    >
                      <Spin size="large" />
                    </div>
                  ) : (
                    <>
                      <Table
                        className="patient-invoice-details-table"
                        dataSource={transactionData
                          .map((item, index) => {
                            const formattedDate = item.date
                              ? dayjs(item.date).format('DD/MM/YY')
                              : '-';
                            const prevDate =
                              index > 0 && transactionData[index - 1]?.date
                                ? dayjs(transactionData[index - 1].date).format(
                                  'DD/MM/YY'
                                )
                                : null;
                            const isDateChange =
                              index > 0 && formattedDate !== prevDate;

                            return {
                              key: item.id?.toString() || index.toString(),
                              id: item.id,
                              date: formattedDate,
                              patientName: item.patient_name || '-',
                              remark: (item as any).remark || '',
                              treatmentName: item.treatment_name || '-',
                              patientStatusType: item.patient_status_type || '',
                              patientServiceType: item.patient_service_type || '',
                              consultation: item.consultation || {
                                final_amount: 0,
                                discount_amount: 0,
                                due_amount: 0,
                              },
                              treatment: item.treatment || {
                                final_amount: 0,
                                discount_amount: 0,
                                due_amount: 0,
                              },
                              medicine: item.medicine || {
                                final_amount: 0,
                                discount_amount: 0,
                                due_amount: 0,
                              },
                              totalAmount: item.total_amount || 0,
                              totalDiscount: item.total_discount || 0,
                              billAmount: (item as any).bill_amount ?? 0,
                              oldPaidAmount: (item as any).old_paid_amount ?? 0,
                              receivedAmountTotal:
                                (item as any).received_amount?.total ?? 0,
                              totalDue: item.total_due || 0,
                              isDateChange,
                              isTotalsRow: false,
                            };
                          })
                          .concat(
                            // Add totals row from API totals (consultation, treatment, total) – use API values, not computed
                            transactionTotals
                              ? [
                                {
                                  key: 'totals-row',
                                  id: '' as unknown as number,
                                  date: '',
                                  patientName: 'Total',
                                  remark: '',
                                  treatmentName: '',
                                  patientStatusType: '',
                                  patientServiceType: '',
                                  // Only Consultation Invoice: use API consultation totals (bill_amount = final amount, total = received)
                                  consultation: {
                                    final_amount:
                                      transactionTotals.consultation.final_amount,
                                    discount_amount:
                                      transactionTotals.consultation.discount_amount,
                                    due_amount:
                                      transactionTotals.consultation.due_amount,
                                    total: (transactionTotals.consultation as { total?: number }).total,
                                    bill_amount: (transactionTotals.consultation as { bill_amount?: number }).bill_amount,
                                  } as any,
                                  // Treatment Invoice section: use API treatment totals (not combined total)
                                  treatment: {
                                    final_amount:
                                      transactionTotals.treatment.final_amount,
                                    discount_amount:
                                      transactionTotals.treatment.discount_amount,
                                    due_amount:
                                      transactionTotals.treatment.due_amount,
                                    total: (transactionTotals.treatment as { total?: number }).total,
                                    bill_amount: (transactionTotals.treatment as { bill_amount?: number }).bill_amount,
                                  } as any,
                                  medicine: {
                                    final_amount: 0,
                                    discount_amount: 0,
                                    due_amount: 0,
                                  },
                                  // Treatment Invoice columns in last row = treatment totals (final_amount, discount, bill_amount, total)
                                  totalAmount:
                                    transactionTotals.treatment.final_amount,
                                  totalDiscount:
                                    transactionTotals.treatment.discount_amount,
                                  billAmount:
                                    (transactionTotals.treatment as { bill_amount?: number }).bill_amount ?? 0,
                                  oldPaidAmount:
                                    (transactionTotals.treatment as { old_paid?: number }).old_paid ?? 0,
                                  receivedAmountTotal:
                                    (transactionTotals.treatment as { total?: number }).total ?? 0,
                                  totalDue: transactionTotals.treatment.due_amount,
                                  isDateChange: false,
                                  isTotalsRow: true,
                                },
                              ]
                              : []
                          )}
                        rowClassName={(record: any) => {
                          if (record.isTotalsRow) return 'totals-row';
                          if (record.isDateChange) return 'date-change-row';
                          return '';
                        }}
                        columns={[
                          {
                            title: 'ID',
                            dataIndex: 'id',
                            key: 'id',
                            width: 40,
                            align: 'center',
                          },
                          {
                            title: 'Date',
                            dataIndex: 'date',
                            key: 'date',
                            width: 75,
                            align: 'center',
                          },
                          {
                            title: 'Patient Name',
                            dataIndex: 'patientName',
                            key: 'patientName',
                            width: 100,
                            render: (_: any, record: any) => {
                              // Skip rendering for totals row
                              if (record.isTotalsRow) {
                                return (
                                  <div
                                    style={{ fontWeight: 'bold', fontSize: '11px' }}
                                  >
                                    {record.patientName}
                                  </div>
                                );
                              }

                              const patientStatusType = record.patientStatusType || '';
                              let statusTag = null;

                              // Use patient_status_type directly (Old/New)
                              if (patientStatusType) {
                                const status = patientStatusType.trim().toLowerCase();
                                if (status === 'old' || status === 'new') {
                                  statusTag = (
                                    <div
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '1px 4px',
                                        borderRadius: '3px',
                                        backgroundColor:
                                          status === 'new'
                                            ? '#e6f7ff'
                                            : '#f5f5f5',
                                        color:
                                          status === 'new'
                                            ? '#1890ff'
                                            : '#8c8c8c',
                                        border: `1px solid ${status === 'new' ? '#91d5ff' : '#d9d9d9'}`,
                                        fontSize: '8px',
                                        fontWeight: 600,
                                        minWidth: '16px',
                                        height: '16px',
                                        marginBottom: '0px',
                                      }}
                                    >
                                      {status === 'new' ? 'N' : 'O'}
                                    </div>
                                  );
                                }
                              }

                              const nameContent = (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                  }}
                                >
                                  {statusTag}
                                  <div style={{ fontSize: '11px' }}>
                                    {record.patientName}
                                  </div>
                                </div>
                              );

                              if (record.remark) {
                                const remarkText = (record.remark || '').replace(/\\n/g, '\n');
                                return (
                                  <Tooltip
                                    title={
                                      <div style={{ whiteSpace: 'pre-line', maxWidth: 320 }}>
                                        {remarkText}
                                      </div>
                                    }
                                  >
                                    {nameContent}
                                  </Tooltip>
                                );
                              }

                              return nameContent;
                            },
                          },
                          {
                            title: 'Treatments',
                            dataIndex: 'treatmentName',
                            key: 'treatmentName',
                            width: 120,
                            render: (text: string) => {
                              if (!text || text === '-') return <div style={{ fontSize: '11px' }}>-</div>;
                              const parts = String(text).split(/\s*\+\s*/).map((s: string) => s.trim()).filter(Boolean);
                              return (
                                <div style={{ fontSize: '11px', whiteSpace: 'pre-line' }}>
                                  {parts.join('\n')}
                                </div>
                              );
                            },
                          },
                          {
                            title: 'Type',
                            dataIndex: 'type',
                            key: 'type',
                            width: 60,
                            align: 'center',
                            render: (type: string, record: any) => {
                              // Skip rendering for totals row
                              if (record.isTotalsRow) return '';

                              const patientServiceType = record.patientServiceType || '';

                              // Helper function to get color config for service type (Aesthetic, Clinical, LHR only)
                              const getServiceTypeConfig = (serviceType: string) => {
                                const cat = serviceType.trim().toUpperCase();
                                if (cat === 'AESTHETIC') {
                                  return {
                                    bgColor: '#f9f0ff',
                                    textColor: '#722ed1',
                                    borderColor: '#d3adf7',
                                  };
                                }
                                if (cat === 'LHR') {
                                  return {
                                    bgColor: '#fff7e6',
                                    textColor: '#fa8c16',
                                    borderColor: '#ffd591',
                                  };
                                }
                                if (cat === 'CLINICAL') {
                                  return {
                                    bgColor: '#e6f7ff',
                                    textColor: '#1890ff',
                                    borderColor: '#91d5ff',
                                  };
                                }
                                // Default for Consultation or unknown
                                return {
                                  bgColor: '#e6f7ff',
                                  textColor: '#1890ff',
                                  borderColor: '#91d5ff',
                                };
                              };



                              const tags: JSX.Element[] = [];

                              // Add service type tag if present (Aesthetic, Clinical, LHR)
                              // Apply priority logic: LHR > Aesthetic > Clinical (show only one)
                              if (patientServiceType) {
                                // Split by comma if multiple service types
                                const serviceTypes = patientServiceType
                                  .split(',')
                                  .map((s: string) => s.trim())
                                  .filter((s: string) => s.length > 0);

                                let serviceTypeToShow = '';

                                if (serviceTypes.length === 0) {
                                  // No valid service types
                                } else if (serviceTypes.length === 1) {
                                  // Single service type → show as is
                                  serviceTypeToShow = serviceTypes[0];
                                } else {
                                  // Multiple service types → apply priority logic
                                  const normalizedTypes = serviceTypes.map((s: string) =>
                                    s.trim().toUpperCase()
                                  );
                                  const hasLHR = normalizedTypes.includes('LHR');
                                  const hasAesthetic = normalizedTypes.includes('AESTHETIC');
                                  const hasClinical = normalizedTypes.includes('CLINICAL');

                                  if (hasLHR) {
                                    // If LHR is present → show only LHR
                                    serviceTypeToShow =
                                      serviceTypes.find(
                                        (s: string) => s.trim().toUpperCase() === 'LHR'
                                      ) || 'LHR';
                                  } else if (hasAesthetic && hasClinical) {
                                    // Clinical + Aesthetic → show only Aesthetic
                                    serviceTypeToShow =
                                      serviceTypes.find(
                                        (s: string) => s.trim().toUpperCase() === 'AESTHETIC'
                                      ) || 'Aesthetic';
                                  } else if (hasAesthetic) {
                                    // Only Aesthetic
                                    serviceTypeToShow =
                                      serviceTypes.find(
                                        (s: string) => s.trim().toUpperCase() === 'AESTHETIC'
                                      ) || 'Aesthetic';
                                  } else if (hasClinical) {
                                    // Only Clinical
                                    serviceTypeToShow =
                                      serviceTypes.find(
                                        (s: string) => s.trim().toUpperCase() === 'CLINICAL'
                                      ) || 'Clinical';
                                  } else {
                                    // Unknown types → show first one
                                    serviceTypeToShow = serviceTypes[0];
                                  }
                                }

                                // Render the single service type tag
                                if (serviceTypeToShow) {
                                  const serviceConfig = getServiceTypeConfig(serviceTypeToShow);
                                  tags.push(
                                    <span
                                      key="service"
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '3px 6px',
                                        borderRadius: '4px',
                                        backgroundColor: serviceConfig.bgColor,
                                        color: serviceConfig.textColor,
                                        border: `1px solid ${serviceConfig.borderColor}`,
                                        fontSize: '9px',
                                        fontWeight: 500,
                                        minHeight: '20px',
                                        minWidth: '60px',
                                        maxWidth: '80px',
                                        textAlign: 'center',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {serviceTypeToShow}
                                    </span>
                                  );
                                }
                              }

                              // Fallback to type if no status or service type
                              if (tags.length === 0) {
                                if (!type || type === '-') return '-';
                                return (
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      padding: '3px 6px',
                                      borderRadius: '4px',
                                      backgroundColor: '#f5f5f5',
                                      color: '#8c8c8c',
                                      border: '1px solid #d9d9d9',
                                      fontSize: '9px',
                                      fontWeight: 500,
                                      minHeight: '20px',
                                      minWidth: '60px',
                                      textAlign: 'center',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {type}
                                  </span>
                                );
                              }

                              // Render tags
                              return (
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '6px',
                                    width: '100%',
                                  }}
                                >
                                  {tags}
                                </div>
                              );
                            },
                          },
                          {
                            title: 'Only Consultation Invoice',
                            children: [
                              {
                                title: (
                                  <Text strong style={{ fontSize: '11px' }}>
                                    Total Amount
                                  </Text>
                                ),
                                key: 'consultationFinalAmount',
                                width: 80,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const value =  record.consultation?.final_amount;
                                  if (
                                    value === null ||
                                    value === undefined ||
                                    (value === 0 && !record.isTotalsRow)
                                  )
                                    return record.isTotalsRow ? formatCurrency(value ?? 0) : '';
                                  return (
                                    <Text
                                      strong={record.isTotalsRow}
                                      style={{ fontSize: '9px' }}
                                    >
                                      {formatCurrency(value)}
                                    </Text>
                                  );
                                },
                              },
                              {
                                title: (
                                  <Text
                                    strong
                                    style={{ color: '#fa8c16', fontSize: '11px' }}
                                  >
                                    Discount
                                  </Text>
                                ),
                                key: 'consultationDiscount',
                                width: 80,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const discountAmount =
                                    record.consultation?.discount_amount;
                                  const discountRate =
                                    record.consultation?.discount_rate;
                                  if (
                                    discountAmount === null ||
                                    discountAmount === undefined ||
                                    discountAmount === 0
                                  )
                                    return '';
                                  const rateText =
                                    discountRate !== null &&
                                      discountRate !== undefined &&
                                      discountRate > 0
                                      ? ` (${discountRate}%)`
                                      : '';
                                  return (
                                    <div style={{ textAlign: 'right' }}>
                                      <Text
                                        strong={record.isTotalsRow}
                                        style={{ color: '#fa8c16', fontSize: '9px', display: 'block' }}
                                      >
                                        {formatCurrency(discountAmount)}
                                      </Text>
                                      {rateText && (
                                        <Text style={{ fontSize: '8px', color: '#fa8c16', display: 'block' }}>
                                          {rateText}
                                        </Text>
                                      )}
                                    </div>
                                  );
                                },
                              },
                              {
                                title: (
                                  <Text strong style={{ fontSize: '11px' }}>
                                    Final Amount
                                  </Text>
                                ),
                                key: 'consultationBillAmount',
                                width: 80,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const value = record.consultation?.bill_amount;
                                  if (
                                    value === null ||
                                    value === undefined ||
                                    (value === 0 && !record.isTotalsRow)
                                  )
                                    return record.isTotalsRow ? formatCurrency(value ?? 0) : '';
                                  return (
                                    <Text
                                      strong={record.isTotalsRow}
                                      style={{ fontSize: '9px' }}
                                    >
                                      {formatCurrency(value)}
                                    </Text>
                                  );
                                },
                              },
                              {
                                title: (
                                  <Text
                                    strong
                                    style={{ color: '#389e0d', fontSize: '11px' }}
                                  >
                                    Received Amount
                                  </Text>
                                ),
                                key: 'consultationReceivedAmount',
                                width: 85,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const value = record.consultation?.total ?? 0;
                                  if (
                                    value === null ||
                                    value === undefined ||
                                    (value === 0 && !record.isTotalsRow)
                                  )
                                    return record.isTotalsRow ? formatCurrency(value ?? 0) : '';
                                  return (
                                    <Text
                                      strong={record.isTotalsRow}
                                      style={{ color: '#389e0d', fontSize: '9px' }}
                                    >
                                      {formatCurrency(value)}
                                    </Text>
                                  );
                                },
                              },
                              {
                                title: (
                                  <Text
                                    strong
                                    style={{ color: '#ff4d4f', fontSize: '11px' }}
                                  >
                                    Due
                                  </Text>
                                ),
                                key: 'consultationDue',
                                width: 70,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const value = record.consultation?.due_amount;
                                  if (
                                    value === null ||
                                    value === undefined ||
                                    value === 0
                                  )
                                    return '';
                                  return (
                                    <Text
                                      strong={record.isTotalsRow}
                                      style={{ color: '#ff4d4f', fontSize: '9px' }}
                                    >
                                      {formatCurrency(value)}
                                    </Text>
                                  );
                                },
                              },
                            ],
                          },
                          {
                            title: 'Treatment Invoice',
                            children: [
                              {
                                title: (
                                  <Text strong style={{ fontSize: '11px' }}>
                                    Total Amount
                                  </Text>
                                ),
                                key: 'totalAmount',
                                width: 85,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const value = record.isTotalsRow ? record.totalAmount : (record.treatment?.final_amount ?? 0);
                                  if (
                                    value === null ||
                                    value === undefined ||
                                    (value === 0 && !record.isTotalsRow)
                                  )
                                    return record.isTotalsRow ? formatCurrency(value) : '';
                                  return (
                                    <Text strong={record.isTotalsRow} style={{ fontSize: '9px' }}>
                                      {formatCurrency(value)}
                                    </Text>
                                  );
                                },
                              },
                              {
                                title: (
                                  <Text strong style={{ color: '#fa8c16', fontSize: '11px' }}>
                                    Discount
                                  </Text>
                                ),
                                key: 'totalDiscount',
                                width: 95,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const discountAmount = record.isTotalsRow ? record.totalDiscount : (record.treatment?.discount_amount ?? 0);
                                  const totalAmount = record.isTotalsRow ? record.totalAmount : (record.treatment?.final_amount ?? 0);
                                  const discountRate =
                                    totalAmount > 0 && discountAmount != null
                                      ? Math.round((Number(discountAmount) / Number(totalAmount)) * 10000) / 100
                                      : null;
                                  if (
                                    discountAmount === null ||
                                    discountAmount === undefined ||
                                    (discountAmount === 0 && !record.isTotalsRow)
                                  )
                                    return record.isTotalsRow ? formatCurrency(discountAmount) : '';
                                  const rateText =
                                    record.isTotalsRow || discountRate == null
                                      ? ''
                                      : ` (${discountRate}%)`;
                                  return (
                                    <div style={{ textAlign: 'right' }}>
                                      <Text
                                        strong={record.isTotalsRow}
                                        style={{ color: '#fa8c16', fontSize: '9px', display: 'block' }}
                                      >
                                        {formatCurrency(discountAmount)}
                                      </Text>
                                      {rateText ? (
                                        <Text style={{ fontSize: '8px', color: '#fa8c16', display: 'block' }}>
                                          {rateText}
                                        </Text>
                                      ) : null}
                                    </div>
                                  );
                                },
                              },
                              {
                                title: (
                                  <Text strong style={{ fontSize: '11px' }}>
                                    Final Amount
                                  </Text>
                                ),
                                key: 'billAmount',
                                width: 85,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const value = record.isTotalsRow ? record.billAmount : (record.treatment?.bill_amount ?? 0);
                                  if (
                                    value === null ||
                                    value === undefined ||
                                    (value === 0 && !record.isTotalsRow)
                                  )
                                    return record.isTotalsRow ? formatCurrency(value) : '';
                                  return (
                                    <Text strong={record.isTotalsRow} style={{ fontSize: '9px' }}>
                                      {formatCurrency(value)}
                                    </Text>
                                  );
                                },
                              },
                              {
                                title: (
                                  <Text strong style={{ fontSize: '11px' }}>
                                    Already Paid Amount
                                  </Text>
                                ),
                                key: 'oldPaidAmount',
                                width: 80,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const value = record.isTotalsRow ? record.oldPaidAmount : (record.treatment?.old_paid ?? 0);
                                  if (
                                    value === null ||
                                    value === undefined ||
                                    (value === 0 && !record.isTotalsRow)
                                  )
                                    return record.isTotalsRow ? formatCurrency(value) : '';
                                  return (
                                    <Text strong={record.isTotalsRow} style={{ fontSize: '9px' }}>
                                      {formatCurrency(value)}
                                    </Text>
                                  );
                                },
                              },
                              {
                                title: (
                                  <Text strong style={{ color: '#389e0d', fontSize: '11px' }}>
                                    Received Amount
                                  </Text>
                                ),
                                key: 'receivedAmountTotal',
                                width: 85,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const value = record.isTotalsRow ? record.receivedAmountTotal : (record.treatment?.total ?? 0);
                                  if (
                                    value === null ||
                                    value === undefined ||
                                    (value === 0 && !record.isTotalsRow)
                                  )
                                    return record.isTotalsRow ? formatCurrency(value) : '';
                                  return (
                                    <Text
                                      strong={record.isTotalsRow}
                                      style={{ color: '#389e0d', fontSize: '9px' }}
                                    >
                                      {formatCurrency(value)}
                                    </Text>
                                  );
                                },
                              },
                              {
                                title: (
                                  <Text strong style={{ color: '#ff4d4f', fontSize: '11px' }}>
                                    Due
                                  </Text>
                                ),
                                key: 'totalDue',
                                width: 70,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const value = record.isTotalsRow ? record.totalDue : (record.treatment?.due_amount ?? 0);
                                  if (
                                    value === null ||
                                    value === undefined ||
                                    (value === 0 && !record.isTotalsRow)
                                  )
                                    return record.isTotalsRow ? formatCurrency(value) : '';
                                  return (
                                    <Text
                                      strong={record.isTotalsRow}
                                      style={{ color: '#ff4d4f', fontSize: '9px' }}
                                    >
                                      {formatCurrency(value)}
                                    </Text>
                                  );
                                },
                              },
                            ],
                          },
                        ]}
                        pagination={false}
                        size="small"
                        scroll={{ x: 1400, y: 'calc(100vh - 160px)' }}
                        sticky={{ offsetHeader: 0 }}
                      />
                    </>
                  )}
                </div>
              </Card>
            </Col>
          </Row>

          {/* Medical Invoice Details Row */}
          <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
            <Col xs={24}>
              <Card
                style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
                bodyStyle={{ padding: 0 }}
              >
                <div
                  className="patient-invoice-header"
                  style={{
                    background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                    padding: '16px 20px',
                    borderRadius: '8px 8px 0 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                  >
                    <DollarOutlined style={{ fontSize: '24px', color: '#fff' }} />
                    <Text strong style={{ fontSize: '18px', color: '#fff' }}>
                      Patients with Medical Invoice Details
                    </Text>
                  </div>
                </div>

                <div style={{ padding: 0, background: '#fff', overflowX: 'auto' }}>
                  {!hasUserSelectedDate ? (
                    <div style={{ padding: '16px 20px', textAlign: 'center' }}>
                      <Text type="secondary" style={{ fontSize: '14px' }}>
                        Please select a date range from the filter above to view Patients with Medical Invoice Details
                      </Text>
                    </div>
                  ) : transactionError ? (
                    <div style={{ padding: '16px 20px' }}>
                      <Alert
                        message="Error Loading Transaction Report"
                        description={transactionError}
                        type="error"
                        showIcon
                        action={
                          <button
                            onClick={fetchTransactionReport}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#1890ff',
                            }}
                          >
                            Retry
                          </button>
                        }
                      />
                    </div>
                  ) : transactionLoading ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '200px',
                      }}
                    >
                      <Spin size="large" />
                    </div>
                  ) : (
                    <>
                      <Table
                        className="patient-invoice-details-table"
                        dataSource={medicalTransactionData
                          .map((item, index) => {
                            const formattedDate = item.date
                              ? dayjs(item.date).format('DD/MM/YY')
                              : '-';
                            const prevDate =
                              index > 0 && medicalTransactionData[index - 1]?.date
                                ? dayjs(medicalTransactionData[index - 1].date).format(
                                  'DD/MM/YY'
                                )
                                : null;
                            const isDateChange =
                              index > 0 && formattedDate !== prevDate;

                            return {
                              key: item.id?.toString() || index.toString(),
                              id: item.id,
                              date: formattedDate,
                              patientName: item.patient_name || '-',
                              treatmentName: item.treatment_name || '-',
                              patientStatusType: item.patient_status_type || '',
                              patientServiceType: item.patient_service_type || '',
                              consultation: item.consultation || {
                                final_amount: 0,
                                discount_amount: 0,
                                due_amount: 0,
                              },
                              treatment: item.treatment || {
                                final_amount: 0,
                                discount_amount: 0,
                                due_amount: 0,
                              },
                              medicine: {
                                ...(item.medicine || {
                                  final_amount: 0,
                                  discount_amount: 0,
                                  due_amount: 0,
                                }),
                                bill_amount: item.medicine?.bill_amount,
                                total: item.medicine?.total,
                              },
                              totalAmount: item.total_amount || 0,
                              totalDiscount: item.total_discount || 0,
                              totalDue: item.total_due || 0,
                              isDateChange,
                              isTotalsRow: false,
                            };
                          })
                          .concat(
                            medicalTransactionTotals
                              ? [
                                {
                                  key: 'totals-row',
                                  id: '' as unknown as number,
                                  date: '',
                                  patientName: '',
                                  treatmentName: '',
                                  patientStatusType: '',
                                  patientServiceType: '',
                                  consultation: {
                                    final_amount: 0,
                                    discount_amount: 0,
                                    due_amount: 0,
                                  },
                                  treatment: {
                                    final_amount: 0,
                                    discount_amount: 0,
                                    due_amount: 0,
                                  },
                                  medicine: {
                                    final_amount:
                                      medicalTransactionTotals.medicine.final_amount,
                                    discount_amount:
                                      medicalTransactionTotals.medicine.discount_amount,
                                    due_amount:
                                      medicalTransactionTotals.medicine.due_amount,
                                    bill_amount:
                                      medicalTransactionTotals.medicine?.bill_amount
                                      ?? medicalTransactionTotals.total?.bill_amount,
                                    total: medicalTransactionTotals.medicine?.total,
                                  },
                                  totalAmount:
                                    medicalTransactionTotals.total.final_amount,
                                  totalDiscount:
                                    medicalTransactionTotals.total.discount_amount,
                                  totalDue: medicalTransactionTotals.total.due_amount,
                                  isDateChange: false,
                                  isTotalsRow: true,
                                },
                              ]
                              : []
                          )}
                        rowClassName={(record: any) => {
                          if (record.isTotalsRow) return 'totals-row';
                          if (record.isDateChange) return 'date-change-row';
                          return '';
                        }}
                        columns={[
                          {
                            title: 'ID',
                            dataIndex: 'id',
                            key: 'id',
                            width: 40,
                            align: 'center',
                          },
                          {
                            title: 'Date',
                            dataIndex: 'date',
                            key: 'date',
                            width: 75,
                            align: 'center',
                          },
                          {
                            title: 'Patient Name',
                            dataIndex: 'patientName',
                            key: 'patientName',
                            width: 100,
                            render: (_: any, record: any) => {
                              if (record.isTotalsRow) {
                                return (
                                  <div
                                    style={{ fontWeight: 'bold', fontSize: '11px' }}
                                  >
                                    {record.patientName}
                                  </div>
                                );
                              }

                              const patientStatusType = record.patientStatusType || '';
                              let statusTag = null;

                              if (patientStatusType) {
                                const status = patientStatusType.trim().toLowerCase();
                                if (status === 'old' || status === 'new') {
                                  statusTag = (
                                    <div
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '1px 4px',
                                        borderRadius: '3px',
                                        backgroundColor:
                                          status === 'new'
                                            ? '#e6f7ff'
                                            : '#f5f5f5',
                                        color:
                                          status === 'new'
                                            ? '#1890ff'
                                            : '#8c8c8c',
                                        border: `1px solid ${status === 'new' ? '#91d5ff' : '#d9d9d9'}`,
                                        fontSize: '8px',
                                        fontWeight: 600,
                                        minWidth: '16px',
                                        height: '16px',
                                        marginBottom: '0px',
                                      }}
                                    >
                                      {status === 'new' ? 'N' : 'O'}
                                    </div>
                                  );
                                }
                              }

                              const nameContent = (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                  }}
                                >
                                  {statusTag}
                                  <div style={{ fontSize: '11px' }}>
                                    {record.patientName}
                                  </div>
                                </div>
                              );

                              if (record.remark) {
                                const remarkText = (record.remark || '').replace(/\\n/g, '\n');
                                return (
                                  <Tooltip
                                    title={
                                      <div style={{ whiteSpace: 'pre-line', maxWidth: 320 }}>
                                        {remarkText}
                                      </div>
                                    }
                                  >
                                    {nameContent}
                                  </Tooltip>
                                );
                              }

                              return nameContent;
                            },
                          },
                          {
                            title: 'Medical Invoice',
                            children: [
                              {
                                title: (
                                  <Text strong style={{ fontSize: '11px' }}>
                                    Total Amount
                                  </Text>
                                ),
                                key: 'medicineTotalAmount',
                                width: 80,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const value = record.totalAmount;
                                  if (
                                    value === null ||
                                    value === undefined ||
                                    (value === 0 && !record.isTotalsRow)
                                  )
                                    return record.isTotalsRow ? formatCurrency(value) : '';
                                  return (
                                    <Text
                                      strong={record.isTotalsRow}
                                      style={{ fontSize: '9px' }}
                                    >
                                      {formatCurrency(value)}
                                    </Text>
                                  );
                                },
                              },
                              {
                                title: (
                                  <Text
                                    strong
                                    style={{ color: '#fa8c16', fontSize: '11px' }}
                                  >
                                    Discount
                                  </Text>
                                ),
                                key: 'medicineDiscount',
                                width: 80,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const discountAmount =
                                    record.medicine?.discount_amount;
                                  const discountRate = record.medicine?.discount_rate;
                                  if (
                                    discountAmount === null ||
                                    discountAmount === undefined ||
                                    discountAmount === 0
                                  )
                                    return '';
                                  const rateText =
                                    discountRate !== null &&
                                      discountRate !== undefined &&
                                      discountRate > 0
                                      ? ` (${discountRate}%)`
                                      : '';
                                  return (
                                    <div style={{ textAlign: 'right' }}>
                                      <Text
                                        strong={record.isTotalsRow}
                                        style={{ color: '#fa8c16', fontSize: '9px', display: 'block' }}
                                      >
                                        {formatCurrency(discountAmount)}
                                      </Text>
                                      {rateText && (
                                        <Text style={{ fontSize: '8px', color: '#fa8c16', display: 'block' }}>
                                          {rateText}
                                        </Text>
                                      )}
                                    </div>
                                  );
                                },
                              },
                              {
                                title: (
                                  <Text strong style={{ fontSize: '11px' }}>
                                    Final Amount
                                  </Text>
                                ),
                                key: 'medicineFinalAmount',
                                width: 80,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const value = record.medicine?.bill_amount ?? record.medicine?.final_amount;
                                  if (
                                    value === null ||
                                    value === undefined ||
                                    (value === 0 && !record.isTotalsRow)
                                  )
                                    return record.isTotalsRow ? formatCurrency(value) : '';
                                  return (
                                    <Text
                                      strong={record.isTotalsRow}
                                      style={{ fontSize: '9px' }}
                                    >
                                      {formatCurrency(value)}
                                    </Text>
                                  );
                                },
                              },
                              {
                                title: (
                                  <Text
                                    strong
                                    style={{ color: '#389e0d', fontSize: '11px' }}
                                  >
                                    Received Amount
                                  </Text>
                                ),
                                key: 'medicineReceivedAmount',
                                width: 85,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const value = record.medicine?.total;
                                  if (
                                    value === null ||
                                    value === undefined ||
                                    (value === 0 && !record.isTotalsRow)
                                  )
                                    return record.isTotalsRow ? formatCurrency(value ?? 0) : '';
                                  return (
                                    <Text
                                      strong={record.isTotalsRow}
                                      style={{ color: '#389e0d', fontSize: '9px' }}
                                    >
                                      {formatCurrency(value)}
                                    </Text>
                                  );
                                },
                              },
                              {
                                title: (
                                  <Text
                                    strong
                                    style={{ color: '#ff4d4f', fontSize: '11px' }}
                                  >
                                    Due
                                  </Text>
                                ),
                                key: 'medicineDue',
                                width: 70,
                                align: 'right',
                                render: (_: any, record: any) => {
                                  const value = record.medicine?.due_amount;
                                  if (
                                    value === null ||
                                    value === undefined ||
                                    value === 0
                                  )
                                    return '';
                                  return (
                                    <Text
                                      strong={record.isTotalsRow}
                                      style={{ color: '#ff4d4f', fontSize: '9px' }}
                                    >
                                      {formatCurrency(value)}
                                    </Text>
                                  );
                                },
                              },
                            ],
                          },
                        ]}
                        pagination={false}
                        size="small"
                        scroll={{ x: 1400, y: 'calc(100vh - 160px)' }}
                        sticky={{ offsetHeader: 0 }}
                      />
                    </>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        .patient-invoice-details-table {
          font-size: 10px !important;
          width: 100% !important;
          overflow-x: auto !important;
        }

        .patient-invoice-details-table .ant-table-thead > tr > th {
          font-size: 12px !important;
          padding: 10px 6px !important;
          font-weight: 600 !important;
          background: #fafafa !important;
          border-bottom: 1px solid #d9d9d9 !important;
          position: sticky !important;
          top: 0 !important;
          z-index: 2 !important;
        }

        .patient-invoice-details-table .ant-table-tbody > tr > td {
          font-size: 11px !important;
          padding: 6px 6px !important;
          border-bottom: 1px solid #f0f0f0 !important;
        }

        .patient-invoice-details-table .totals-row {
          background-color: #f8f9fa !important;
          border-top: 2px solid #dee2e6 !important;
          font-weight: bold !important;
          position: sticky !important;
          bottom: 0 !important;
          z-index: 1 !important;
        }

        .patient-invoice-details-table .totals-row td {
          background-color: #f8f9fa !important;
          border-top: 2px solid #dee2e6 !important;
          font-weight: bold !important;
          font-size: 11px !important;
        }

        .patient-invoice-details-table .ant-table-container {
          max-height: calc(100vh - 200px) !important;
        }

        .patient-invoice-details-table .ant-table-body {
          max-height: calc(100vh - 250px) !important;
          overflow-y: auto !important;
        }

        /* Patients Visits table borders */
        .dashboard-container .patients-visits-table .ant-table-tbody > tr.patients-visits-border-bottom > td {
          border-bottom: 2px solid #bfbfbf !important;
        }

        .dashboard-container .patients-visits-table .ant-table-tbody > tr.patients-visits-border-bottom {
          border-bottom: 2px solid #bfbfbf !important;
        }

        /* Accounting table borders - make them visible */
        .dashboard-container .sale-module-table .ant-table-tbody > tr.accounting-border-top > td {
          border-top: 2px solid #bfbfbf !important;
        }

        .dashboard-container .sale-module-table .ant-table-tbody > tr.accounting-border-bottom > td {
          border-bottom: 2px solid #bfbfbf !important;
        }

        /* Ensure borders override default table styles */
        .dashboard-container .sale-module-table .ant-table-tbody > tr.accounting-border-top > td:first-child,
        .dashboard-container .sale-module-table .ant-table-tbody > tr.accounting-border-top > td:last-child {
          border-top: 2px solid #bfbfbf !important;
        }

        .dashboard-container .sale-module-table .ant-table-tbody > tr.accounting-border-bottom > td:first-child,
        .dashboard-container .sale-module-table .ant-table-tbody > tr.accounting-border-bottom > td:last-child {
          border-bottom: 2px solid #bfbfbf !important;
        }

        /* Purchase Module and Sale Module font sizes */
        .dashboard-container .purchase-module-table .ant-table-thead > tr > th {
          font-size: 11px !important;
        }

        .dashboard-container .purchase-module-table .ant-table-tbody > tr > td {
          font-size: 10px !important;
        }

        .dashboard-container .sale-module-table .ant-table-thead > tr > th {
          font-size: 11px !important;
        }

        .dashboard-container .sale-module-table .ant-table-tbody > tr > td {
          font-size: 10px !important;
        }
      `,
        }}
      />
    </div>
  );
};

export default DashboardStats;
